import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faPlus,
  faTrash,
  faSyncAlt,
  faUpload,
  faEye,
  faChevronDown,
  faChevronUp,
  faSave,
  faClipboardCheck,
  faUndo,
  faHospital,
  faUserDoctor,
  faExpand,
  faCompress,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../../config';
import VatComputationBreakdown from '../../../components/claims/VatComputationBreakdown';

const allowedFileTypes = ['pdf', 'jpg', 'jpeg', 'png'];
const maxFileSizeMB = 80;
const maxUploadLimit = 10;
const HMC_VAT_PROVIDER_KEYWORDS = [
  'THE MEDICAL CITY CLINIC',
  'HEALTHWAY',
  'AMAPHIL MEDICAL CENTER - TEST',
];
const HMC_VAT_OPTIONS = [
  { value: '1', label: 'With VAT in Billing Invoice' },
  { value: '0', label: 'No VAT in Billing Invoice' },
];
const FLOW_TYPE_1_COVERAGES = [
  'OUTPATIENT-BASIC-CONSULTATION',
  'OUTPATIENT-REFERRAL-TO-SPECIALIST',
  'MATERNITY-BASIC-CONSULTATION',
];
const FLOW_TYPE_1_DEFAULT_PROFESSIONAL_FEE = 600;
const FLOW_TYPE_1_MAX_PHYSICIANS = 2;

const CLAIM_STATUS_META = {
  'FOR PROCESSING': {
    label: 'For Processing',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  PROCESSED: {
    label: 'Processed',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  EXTRACTED: {
    label: 'Extracted',
    className: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  PAID: {
    label: 'Paid',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  RTP: {
    label: 'RTP',
    className: 'bg-red-50 text-red-700 border-red-200',
  },
  'N/A': {
    label: 'n/a',
    className: 'bg-slate-50 text-slate-600 border-slate-200',
  },
};

const getClaimStatusMeta = (claimStatus, rtpTag) => {
  const normalized = String(claimStatus || '').trim().toUpperCase();
  if (Number(rtpTag || 0) === 1 || normalized === 'FOR RTP') {
    return CLAIM_STATUS_META.RTP;
  }

  return CLAIM_STATUS_META[normalized] || CLAIM_STATUS_META['N/A'];
};

const isHmcVatApplicableProvider = (providerName) => {
  const normalizedProviderName = String(providerName || '').trim().toUpperCase();
  if (!normalizedProviderName) return false;

  return HMC_VAT_PROVIDER_KEYWORDS.some((keyword) =>
    normalizedProviderName.includes(keyword)
  );
};

const ProcessClaimModal = ({
  isOpen,
  onClose,
  claim,
  mode = 'edit',
  showReturnButton = false,
  variant = 'modal',
  sourceTab = null,
  onSaved = null,
}) => {
  const isFullPage = variant === 'fullpage';
  const isBatchProcessingMode = sourceTab === 'batch-for-processing';
  const isReadOnly = mode === 'view';
  const canUpdateRemarksInView =
    isReadOnly && ['processed', 'extracted', 'rtp'].includes(sourceTab);
  const isClaimRemarksReadOnly = isReadOnly && !canUpdateRemarksInView;
  const saveEndpoint = isBatchProcessingMode
    ? '/claims/processing/batch-for-processing/save'
    : '/claims/processing/save';
  const handleClose = () => {
    if (isFullPage) {
      window.close();
    } else if (onClose) {
      onClose();
    }
  };
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [providerContact, setProviderContact] = useState(null);
  const [cptItems, setCptItems] = useState([]);
  const [icdItems, setIcdItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [soaFiles, setSoaFiles] = useState([]);
  const [otherFileReferences, setOtherFileReferences] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [physicianSearch, setPhysicianSearch] = useState('');
  const [physicianOptions, setPhysicianOptions] = useState([]);
  const [showPhysicianOptions, setShowPhysicianOptions] = useState(false);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [onlyAffiliatedPhysicians, setOnlyAffiliatedPhysicians] = useState(true);
  const [specialization, setSpecialization] = useState('');
  const [professionalFee, setProfessionalFee] = useState('');
  const [physicianPage, setPhysicianPage] = useState(1);
  const [physicianPerPage, setPhysicianPerPage] = useState(5);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [rtpEnabled, setRtpEnabled] = useState(false);
  const [rtpReason, setRtpReason] = useState('');
  const [rtpRemarks, setRtpRemarks] = useState('');
  const [rtpReasons, setRtpReasons] = useState([]);
  const [transmittal, setTransmittal] = useState('1');
  const [hmcVatTag, setHmcVatTag] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [diagnosisRemarks, setDiagnosisRemarks] = useState('');
  const [billerReference, setBillerReference] = useState('');
  const [claimRemarks, setClaimRemarks] = useState('');
  const [icdSearchTerm, setIcdSearchTerm] = useState('');
  const [icdOptions, setIcdOptions] = useState([]);
  const [showIcdOptions, setShowIcdOptions] = useState(false);
  const [selectedIcdId, setSelectedIcdId] = useState(null);
  const [showDocuments, setShowDocuments] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showClaimHeaderDetails, setShowClaimHeaderDetails] = useState(false);
  const [isSaveSummaryOpen, setIsSaveSummaryOpen] = useState(false);
  const [isBatchRtpSaveConfirmOpen, setIsBatchRtpSaveConfirmOpen] = useState(false);
  const [saveSummary, setSaveSummary] = useState(null);
  const [saveSteps, setSaveSteps] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isTransmittalConfirmOpen, setIsTransmittalConfirmOpen] = useState(false);
  const [isProcessSummaryOpen, setIsProcessSummaryOpen] = useState(false);
  const [processSummary, setProcessSummary] = useState(null);
  const [processSteps, setProcessSteps] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rtpDetails, setRtpDetails] = useState(null);
  const [rtpAttachments, setRtpAttachments] = useState([]);
  const [isRtpModalOpen, setIsRtpModalOpen] = useState(false);
  const [rtpModalRemarks, setRtpModalRemarks] = useState('');
  const [rtpUploadQueue, setRtpUploadQueue] = useState([]);
  const [rtpUploadFiles, setRtpUploadFiles] = useState([]);
  const [isSubmittingRtp, setIsSubmittingRtp] = useState(false);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [overrideRemarks, setOverrideRemarks] = useState('');
  const [shouldOverrideCancelled, setShouldOverrideCancelled] = useState(false);
  const [isUpdatingRemarks, setIsUpdatingRemarks] = useState(false);
  const [isTrackerRemarksModalOpen, setIsTrackerRemarksModalOpen] = useState(false);
  const [isLoadingTrackerRemarks, setIsLoadingTrackerRemarks] = useState(false);
  const [trackerRemarksRows, setTrackerRemarksRows] = useState([]);
  const [isProviderPaymentSetupModalOpen, setIsProviderPaymentSetupModalOpen] = useState(false);
  const [isLoadingProviderPaymentSetup, setIsLoadingProviderPaymentSetup] = useState(false);
  const [providerPaymentSetupRows, setProviderPaymentSetupRows] = useState([]);
  const [providerPaymentSetupDisplayRows, setProviderPaymentSetupDisplayRows] = useState([]);
  const [providerPaymentSetupProvider, setProviderPaymentSetupProvider] = useState(null);
  const fileInputRef = useRef(null);
  const transmittalConfirmAudioRef = useRef(null);

  const phpFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(String(value).replace(/,/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const parseStrictNumber = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    const parsed = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const hasAmountValue = (value) =>
    value !== null && value !== undefined && value !== '';

  const normalizeCptKeyPart = (value) => String(value ?? '').trim().toUpperCase();

  const getCptDisplayCode = (item) =>
    item?.cpt_code || item?.cpt_pkg_code || item?.provider_cpt_code || '-';

  const getCptRowKey = (item, index) =>
    [
      normalizeCptKeyPart(item?.cpt_code),
      normalizeCptKeyPart(item?.cpt_pkg_code),
      normalizeCptKeyPart(item?.provider_cpt_code),
      item?.id || index,
    ].join('|');

  const isSameCptItem = (left, right) => {
    if (left?.id && right?.id && left.id === right.id) return true;

    const leftProviderCode = normalizeCptKeyPart(left?.provider_cpt_code);
    const rightProviderCode = normalizeCptKeyPart(right?.provider_cpt_code);
    const leftCptCode = normalizeCptKeyPart(left?.cpt_code);
    const rightCptCode = normalizeCptKeyPart(right?.cpt_code);
    const leftPackageCode = normalizeCptKeyPart(left?.cpt_pkg_code);
    const rightPackageCode = normalizeCptKeyPart(right?.cpt_pkg_code);

    if (leftProviderCode && rightProviderCode && leftProviderCode === rightProviderCode) {
      return leftCptCode === rightCptCode && leftPackageCode === rightPackageCode;
    }

    if (leftCptCode && rightCptCode) return leftCptCode === rightCptCode;
    if (leftPackageCode && rightPackageCode) return leftPackageCode === rightPackageCode;

    return false;
  };

  const toCurrencyCents = (value) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return 0;
    return Math.round((parsed + Number.EPSILON) * 100);
  };

  const isDiscountRow = (item) =>
    String(item?.cpt_code || item?.provider_cpt_code || '').toUpperCase() === 'DISCOUNT';

  const toPersistedCptRate = (item, amount) => {
    if (!isDiscountRow(item)) return amount;
    return amount > 0 ? amount * -1 : amount;
  };

  const getCptSum = (items) =>
    items.reduce((sum, item) => {
      const amount = parseNumber(item.final_amount);
      return sum + (isDiscountRow(item) ? Math.abs(amount) * -1 : amount);
    }, 0);

  const getSignedCptAmount = (item, value) => {
    const amount = parseNumber(value);
    if (!isDiscountRow(item)) return amount;
    return amount > 0 ? Math.abs(amount) * -1 : amount;
  };

  const formatNullableCptAmount = (item, fieldName) =>
    hasAmountValue(item?.[fieldName])
      ? phpFormatter.format(getSignedCptAmount(item, item[fieldName]))
      : '-';

  const loaAmount = hasAmountValue(details?.loa_amount) ? parseNumber(details.loa_amount) : 0;
  const hasStoredClaimAmount = hasAmountValue(details?.claim_amount);
  const storedClaimAmount = hasStoredClaimAmount ? parseNumber(details.claim_amount) : 0;
  const normalizedClaimStatus = String(details?.claim_status || claim?.claim_status || '')
    .trim()
    .toUpperCase();
  const isPaidClaim = normalizedClaimStatus === 'PAID' || sourceTab === 'paid';
  const allowClaimAmountOverLoa = Boolean(details?.allow_claim_amount_over_loa);
  const shouldEnforceLoaCap = loaAmount > 0 && !allowClaimAmountOverLoa;
  const isExceedingLoa = (amount) =>
    shouldEnforceLoaCap && toCurrencyCents(amount) > toCurrencyCents(loaAmount);
  const isApeCoverage = String(details?.coverage_code || '').toUpperCase() === 'APE';
  const hasTransmittalOption = Number(details?.spec_transmittal) === 1;
  const isHmcVatApplicable = useMemo(
    () => isHmcVatApplicableProvider(details?.provider_name),
    [details?.provider_name]
  );
  const hmcVatTagLabel =
    hmcVatTag === '1'
      ? 'With VAT in Billing Invoice'
      : hmcVatTag === '0'
      ? 'No VAT in Billing Invoice'
      : '';
  const normalizedCoverageCode = String(details?.coverage_code || '').trim().toUpperCase();
  const isFlowType1Claim = FLOW_TYPE_1_COVERAGES.includes(normalizedCoverageCode);
  const isClinicSettingClaim = normalizedCoverageCode === 'OUTPATIENT-CLINIC-SETTING';

  const cptTotal = useMemo(() => getCptSum(cptItems), [cptItems]);
  const cptDefaultPriceTotal = useMemo(
    () =>
      cptItems.reduce((sum, item) => {
        const amount = parseNumber(item.default_price);
        return sum + (isDiscountRow(item) ? Math.abs(amount) * -1 : amount);
      }, 0),
    [cptItems]
  );
  const cptLoaAmountTotal = useMemo(
    () =>
      cptItems.reduce((sum, item) => {
        if (!hasAmountValue(item.loa_cpt_amt)) return sum;
        return sum + getSignedCptAmount(item, item.loa_cpt_amt);
      }, 0),
    [cptItems]
  );
  const cptClaimsAmountTotal = useMemo(
    () =>
      cptItems.reduce((sum, item) => {
        if (!hasAmountValue(item.claims_cpt_amt)) return sum;
        return sum + getSignedCptAmount(item, item.claims_cpt_amt);
      }, 0),
    [cptItems]
  );
  const physicianTotal = useMemo(
    () =>
      physicians.reduce((sum, item) => {
        const amount = parseNumber(item.amount);
        if (isFlowType1Claim && amount <= 0) {
          return sum + FLOW_TYPE_1_DEFAULT_PROFESSIONAL_FEE;
        }
        return sum + amount;
      }, 0),
    [physicians, isFlowType1Claim]
  );
  const flowType1DefaultFeePhysicians = useMemo(
    () =>
      isFlowType1Claim
        ? physicians.filter((item) => parseNumber(item.amount) <= 0).length
        : 0,
    [physicians, isFlowType1Claim]
  );
  const computedTotalAmount = cptTotal + physicianTotal;
  const processingTotalAmount = isApeCoverage ? loaAmount : computedTotalAmount;
  const displayTotalAmount =
    (isReadOnly || isPaidClaim) && hasStoredClaimAmount
      ? storedClaimAmount
      : processingTotalAmount;
  const totalAmount = processingTotalAmount;
  const normalizedTransmittal = String(transmittal ?? '');
  const isPaidToDoctor = normalizedTransmittal === '0';
  const payoutLabel = isPaidToDoctor ? 'Paid to Doctor' : 'Based on Provider Set-up';
  const payoutSubLabel = isPaidToDoctor ? 'No Transmittal' : 'With Transmittal';
  const payoutDescription = isPaidToDoctor
    ? 'No Transmittal claims are paid to the doctor.'
    : 'With Transmittal follows the Provider Payment Set-up and may become Paid to Hospital/Clinic, Paid to Physician, or Split Type.';

  const paginatedPhysicians = useMemo(() => {
    const start = (physicianPage - 1) * physicianPerPage;
    return physicians.slice(start, start + physicianPerPage);
  }, [physicians, physicianPage, physicianPerPage]);

  const physicianTotalPages = useMemo(() => {
    return physicianPerPage > 0 ? Math.ceil(physicians.length / physicianPerPage) : 1;
  }, [physicians.length, physicianPerPage]);

  const showingPhysiciansText = () => {
    if (physicians.length === 0) return 'Showing 0 to 0 of 0 entries';
    const start = (physicianPage - 1) * physicianPerPage + 1;
    const end = Math.min(physicianPage * physicianPerPage, physicians.length);
    return `Showing ${start} to ${end} of ${physicians.length} entries`;
  };

  const formatPhysicianId = (value) => {
    const normalized = String(value ?? '').trim();
    return normalized && normalized.toLowerCase() !== 'null' ? normalized : 'N/A';
  };

  const getPhysicianDisplayName = (option) =>
    String(option?.full_name || option?.physician_name || '')
      .replace(/\s+/g, ' ')
      .trim();

  const isOptionAffiliatedPhysician = (option) =>
    option?.is_affiliated === true ||
    option?.is_affiliated === 1 ||
    option?.is_affiliated === '1';

  const resolveApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message;
    if (!message) return fallback;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'object') return Object.values(message).flat().join(', ');
    return fallback;
  };

  const formatTrackerDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-PH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCopyTrackerField = async (value, label) => {
    const text = String(value ?? '');
    if (!text.trim()) {
      toast.error(`No ${label.toLowerCase()} to copy.`, { duration: 3000 });
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      toast.success(`${label} copied.`, { duration: 3000 });
    } catch (error) {
      toast.error(`Failed to copy ${label.toLowerCase()}.`, { duration: 4000 });
    }
  };

  const parseBatchDraftPayload = () => {
    const rawPayload = claim?.draft_payload_json || claim?.draft_payload;
    if (!rawPayload) return null;
    if (typeof rawPayload === 'object') return rawPayload;

    try {
      const parsed = JSON.parse(rawPayload);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const overlayDraftCptRows = (loadedRows, draftRows) => {
    if (!Array.isArray(draftRows) || draftRows.length === 0) return loadedRows;

    const normalizeCode = (value) => String(value || '').trim().toUpperCase();
    const findMatchingLoadedRow = (draftRow, index) => {
      const draftKeys = [
        normalizeCode(draftRow.cpt_code),
        normalizeCode(draftRow.provider_cpt_code),
        normalizeCode(draftRow.cpt_pkg_code),
      ].filter(Boolean);

      return (
        loadedRows.find((loadedRow) =>
          [
            normalizeCode(loadedRow.cpt_code),
            normalizeCode(loadedRow.provider_cpt_code),
            normalizeCode(loadedRow.cpt_pkg_code),
          ].some((key) => draftKeys.includes(key))
        ) ||
        loadedRows[index] ||
        {}
      );
    };

    return draftRows.map((draftRow, index) => {
      const loadedRow = findMatchingLoadedRow(draftRow, index);
      const mergedRow = {
        ...loadedRow,
        ...draftRow,
        id: loadedRow.id || draftRow.id || `batch-draft-cpt-${index}`,
        cpt_code: draftRow.cpt_code ?? loadedRow.cpt_code ?? null,
        provider_cpt_code: draftRow.provider_cpt_code ?? loadedRow.provider_cpt_code ?? '',
        cpt_pkg_code: draftRow.cpt_pkg_code ?? loadedRow.cpt_pkg_code ?? null,
      };

      mergedRow.final_amount = hasAmountValue(draftRow.cpt_rate)
        ? isDiscountRow(mergedRow)
          ? Math.abs(parseNumber(draftRow.cpt_rate))
          : draftRow.cpt_rate
        : loadedRow.final_amount ?? '';

      return mergedRow;
    });
  };

  const fetchClaimDetails = async () => {
    if (!claim?.trans_no) return;
    setLoading(true);
    try {
      const [detailsResp, cptResp, icdResp, docsResp, physResp, rtpResp, rtpDetailsResp] =
        await Promise.all([
          axios.get(`${BASE_URL}/claims/processing/details`, {
            params: { trans_no: claim.trans_no },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
          }),
          axios.get(`${BASE_URL}/claims/processing/cpt`, {
            params: { trans_no: claim.trans_no },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
          }),
          axios.get(`${BASE_URL}/claims/processing/icd`, {
            params: { trans_no: claim.trans_no },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
          }),
          axios.get(`${BASE_URL}/claims/processing/documents`, {
            params: { trans_no: claim.trans_no },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
          }),
          axios.get(`${BASE_URL}/claims/processing/physicians`, {
            params: { loa_no: claim.loa_no || claim.trans_no },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
          }),
          axios.get(`${BASE_URL}/claims/processing/rtp-reasons`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
          }),
          axios.get(`${BASE_URL}/claims/processing/rtp/details`, {
            params: { trans_no: claim.trans_no },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
          }),
        ]);

      const detailData = detailsResp.data?.data || null;
      const initialHmcVatTag = detailData?.hmc_vat_tag;
      const requiresHmcVatTag =
        Boolean(detailData?.requires_hmc_vat_tag) ||
        isHmcVatApplicableProvider(detailData?.provider_name);
      const draftPayload = isBatchProcessingMode ? parseBatchDraftPayload() : null;
      const loadedCptRows = (cptResp.data?.data || []).map((item) => ({
        ...item,
        final_amount: isDiscountRow(item)
          ? Math.abs(parseNumber(item.cpt_rate ?? item.default_price ?? 0))
          : item.cpt_rate ?? item.default_price ?? 0,
      }));
      setDetails(detailData);
      setProviderContact(detailsResp.data?.provider_contact || null);
      setCptItems(overlayDraftCptRows(loadedCptRows, draftPayload?.cpt_rows));
      const icdList = icdResp.data?.data || [];
      const otherFilesReference = Array.isArray(docsResp.data?.other_files_reference)
        ? docsResp.data.other_files_reference.filter(
            (item) => item && item.url && item.file_name
          )
        : [];
      setIcdItems(icdList);
      setAttachments(docsResp.data?.attachments || []);
      setSoaFiles(docsResp.data?.soa_files || []);
      setOtherFileReferences(otherFilesReference);
      setPhysicians(physResp.data?.data || []);
      setRtpReasons(rtpResp.data?.data || []);
      setRtpDetails(rtpDetailsResp.data?.rtp || null);
      setRtpAttachments(rtpDetailsResp.data?.attachments || []);

      const initialDiagnosis = detailData?.diagnosis || '';
      const firstIcd = icdList[0] || null;
      const fallbackDiagnosis = firstIcd
        ? firstIcd.condition || firstIcd.icd_10_cm || firstIcd.icd_9_cm || ''
        : '';
      const normalizedInitialDiagnosis = String(initialDiagnosis).trim().toUpperCase();
      const matchedInitialIcd = normalizedInitialDiagnosis
        ? icdList.find((item) =>
            [item.condition, item.icd_10_cm, item.icd_9_cm].some(
              (value) => String(value || '').trim().toUpperCase() === normalizedInitialDiagnosis
            )
          )
        : null;
      const resolvedDiagnosis = initialDiagnosis || fallbackDiagnosis;

      setChiefComplaint(detailData?.chief_complaint || '');
      setDiagnosis(draftPayload?.diagnosis ?? resolvedDiagnosis);
      setDiagnosisRemarks(draftPayload?.diagnosis_remarks ?? detailData?.diagnosis_remarks ?? '');
      setBillerReference(
        draftPayload?.biller_reference ?? detailData?.bill_no ?? detailData?.soa_no ?? detailData?.soa ?? ''
      );
      setClaimRemarks(draftPayload?.claim_remarks ?? detailData?.claim_remarks ?? '');
      setRtpEnabled(Boolean(draftPayload?.rtp_tag ?? detailData?.rtp_tag));
      setRtpReason(draftPayload?.rtp_code ?? '');
      setRtpRemarks(draftPayload?.rtp_remarks ?? '');

      const resolvedTransmittal = draftPayload?.transmittal ?? detailData?.transmittal;
      setTransmittal(
        resolvedTransmittal === null || resolvedTransmittal === undefined || resolvedTransmittal === ''
          ? '1'
          : String(resolvedTransmittal)
      );
      const draftHmcVatTag = draftPayload?.hmc_vat_tag;
      setHmcVatTag(
        requiresHmcVatTag &&
          (draftHmcVatTag === 0 ||
            draftHmcVatTag === 1 ||
            draftHmcVatTag === '0' ||
            draftHmcVatTag === '1' ||
            initialHmcVatTag === 0 ||
            initialHmcVatTag === 1 ||
            initialHmcVatTag === '0' ||
            initialHmcVatTag === '1')
          ? String(draftHmcVatTag ?? initialHmcVatTag)
          : ''
      );
      setSelectedIcdId(
        draftPayload?.icd_code ??
          detailData?.icd_code ??
          matchedInitialIcd?.tbl_id ??
          (!initialDiagnosis ? firstIcd?.tbl_id ?? null : null)
      );
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to load claim details.'), {
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen || isFullPage) {
      fetchClaimDetails();
    }
  }, [isOpen, isFullPage, claim?.trans_no]);

  useEffect(() => {
    if (details?.trans_flag_status !== '04') {
      setShouldOverrideCancelled(false);
      setOverrideRemarks('');
      setIsOverrideModalOpen(false);
    }
  }, [details?.trans_flag_status]);

  useEffect(() => {
    if (!isHmcVatApplicable && hmcVatTag !== '') {
      setHmcVatTag('');
    }
  }, [isHmcVatApplicable, hmcVatTag]);

  useEffect(() => {
    if (isOpen) return;
    setIsSaveSummaryOpen(false);
    setSaveSummary(null);
    setSaveSteps([]);
    setIsTransmittalConfirmOpen(false);
    setIsProcessSummaryOpen(false);
    setProcessSummary(null);
    setProcessSteps([]);
    setIsRtpModalOpen(false);
    setRtpModalRemarks('');
    setRtpUploadQueue([]);
    setRtpUploadFiles([]);
    setIsOverrideModalOpen(false);
    setOverrideRemarks('');
    setShouldOverrideCancelled(false);
    setIsUpdatingRemarks(false);
    setIsTrackerRemarksModalOpen(false);
    setIsLoadingTrackerRemarks(false);
    setTrackerRemarksRows([]);
    setIsProviderPaymentSetupModalOpen(false);
    setIsLoadingProviderPaymentSetup(false);
    setProviderPaymentSetupRows([]);
    setProviderPaymentSetupDisplayRows([]);
    setProviderPaymentSetupProvider(null);
  }, [isOpen]);

  const resetModalState = () => {
    setLoading(false);
    setDetails(null);
    setProviderContact(null);
    setCptItems([]);
    setIcdItems([]);
    setAttachments([]);
    setSoaFiles([]);
    setPhysicians([]);
    setPhysicianSearch('');
    setPhysicianOptions([]);
    setShowPhysicianOptions(false);
    setSelectedPhysician(null);
    setOnlyAffiliatedPhysicians(true);
    setSpecialization('');
    setProfessionalFee('');
    setPhysicianPage(1);
    setPhysicianPerPage(5);
    setUploadQueue([]);
    setIsDragging(false);
    setRtpEnabled(false);
    setRtpReason('');
    setRtpRemarks('');
    setRtpReasons([]);
    setTransmittal('1');
    setHmcVatTag('');
    setChiefComplaint('');
    setDiagnosis('');
    setDiagnosisRemarks('');
    setBillerReference('');
    setClaimRemarks('');
    setIcdSearchTerm('');
    setIcdOptions([]);
    setShowIcdOptions(false);
    setSelectedIcdId(null);
    setShowDocuments(true);
    setShowClaimHeaderDetails(false);
    setIsSaveSummaryOpen(false);
    setSaveSummary(null);
    setSaveSteps([]);
    setIsSaving(false);
    setIsTransmittalConfirmOpen(false);
    setIsProcessSummaryOpen(false);
    setProcessSummary(null);
    setProcessSteps([]);
    setIsProcessing(false);
    setIsOverrideModalOpen(false);
    setOverrideRemarks('');
    setShouldOverrideCancelled(false);
    setIsUpdatingRemarks(false);
    setIsTrackerRemarksModalOpen(false);
    setIsLoadingTrackerRemarks(false);
    setTrackerRemarksRows([]);
    setIsProviderPaymentSetupModalOpen(false);
    setIsLoadingProviderPaymentSetup(false);
    setProviderPaymentSetupRows([]);
    setProviderPaymentSetupDisplayRows([]);
    setProviderPaymentSetupProvider(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isTransmittalConfirmOpen) return;

    if (!transmittalConfirmAudioRef.current) {
      transmittalConfirmAudioRef.current = new Audio('/mixkit-software-interface-back-2575.wav');
      transmittalConfirmAudioRef.current.preload = 'auto';
    }

    const audio = transmittalConfirmAudioRef.current;
    audio.currentTime = 0;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [isTransmittalConfirmOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!details?.provider_code) return;
    const controller = new AbortController();
    const term = physicianSearch.trim();
    const timeout = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}/claims/processing/physicians/search`, {
          params: {
            provider_code: details.provider_code,
            search: term,
            affiliated: onlyAffiliatedPhysicians ? 1 : 0,
            limit: 20,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
          signal: controller.signal,
        });
        setPhysicianOptions(response.data?.data || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          toast.error(resolveApiErrorMessage(error, 'Failed to fetch physicians.'), {
            duration: 4000,
          });
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [physicianSearch, onlyAffiliatedPhysicians, isOpen, details?.provider_code]);

  useEffect(() => {
    if (!isOpen) return;
    const term = icdSearchTerm.trim();
    if (!term) {
      setIcdOptions([]);
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}/claims/processing/icd/search`, {
          params: { query: term },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
          signal: controller.signal,
        });
        setIcdOptions(response.data?.data || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          toast.error(resolveApiErrorMessage(error, 'Failed to search ICD.'), { duration: 4000 });
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [icdSearchTerm, isOpen]);

  const handleFinalAmountChange = (index, value) => {
    const parsed = parseStrictNumber(value);
    const next = [...cptItems];
    const previous = next[index]?.final_amount ?? 0;
    next[index] = { ...next[index], final_amount: value };

    const nextTotal = getCptSum(next) + physicianTotal;
    if (isExceedingLoa(nextTotal)) {
      toast.error('Total amount cannot exceed LOA amount.', { duration: 4000 });
      next[index] = { ...next[index], final_amount: previous };
      setCptItems(next);
      return;
    }

    if (Number.isNaN(parsed)) {
      toast.error('Invalid amount.', { duration: 3000 });
      return;
    }
    if (parsed < 0) {
      toast.error('Final amount cannot be negative.', { duration: 3000 });
      next[index] = { ...next[index], final_amount: previous };
      setCptItems(next);
      return;
    }
    setCptItems(next);
  };

  const handleAddSpecialCpt = (type) => {
    if (!details?.trans_no) {
      toast.error('Missing transaction number.', { duration: 4000 });
      return;
    }

    const isDiscount = type === 'discount';
    const rowId = `${type}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const newRow = {
      id: rowId,
      isNew: true,
      cpt_code: isDiscount ? 'DISCOUNT' : '190516327',
      provider_cpt_code: isDiscount ? 'DISCOUNT' : '190516327',
      cpt_desc: isDiscount ? 'Discount' : "Reader's Fee",
      cpt_rate: '',
      final_amount: '',
    };

    setCptItems((prev) => [newRow, ...prev]);
  };

  const handleAddCpt = async (item) => {
    if (!details?.trans_no || !details?.loa_no) {
      toast.error('Missing transaction or LOA number.', { duration: 4000 });
      return;
    }

    if (item.final_amount === '' || item.final_amount === null || item.final_amount === undefined) {
      toast.error('Final amount is required.', { duration: 4000 });
      return;
    }
    const amount = parseStrictNumber(item.final_amount);
    if (Number.isNaN(amount)) {
      toast.error('Invalid amount.', { duration: 4000 });
      return;
    }
    if (amount < 0) {
      toast.error('Final amount cannot be negative.', { duration: 4000 });
      return;
    }

    const nextTotal = getCptSum(
      cptItems.map((row) =>
        isSameCptItem(row, item) ? { ...row, final_amount: amount } : row
      )
    ) + physicianTotal;

    if (isExceedingLoa(nextTotal)) {
      toast.error('Total amount cannot exceed LOA amount.', { duration: 4000 });
      return;
    }

    try {
      const persistedRate = toPersistedCptRate(item, amount);

      await axios.post(
        `${BASE_URL}/claims/processing/cpt/add`,
        {
          trans_no: details.trans_no,
          loa_no: details.loa_no,
          cpt_code: item.cpt_code || null,
          provider_cpt_code: item.provider_cpt_code,
          cpt_pkg_code: item.cpt_pkg_code || null,
          cpt_desc: item.cpt_desc,
          cpt_rate: persistedRate,
          claim_amount: nextTotal,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      setCptItems((prev) =>
        prev.map((row) =>
          isSameCptItem(row, item)
            ? {
                ...row,
                isNew: false,
                cpt_rate: persistedRate,
                claims_cpt_amt: persistedRate,
                final_amount: amount,
              }
            : row
        )
      );
      toast.success('CPT added.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to add CPT.'), { duration: 4000 });
    }
  };

  const handleUpdateCpt = async (item) => {
    if (!details?.trans_no || !details?.loa_no) {
      toast.error('Missing transaction or LOA number.', { duration: 4000 });
      return;
    }

    if (item.final_amount === '' || item.final_amount === null || item.final_amount === undefined) {
      toast.error('Final amount is required.', { duration: 4000 });
      return;
    }
    const amount = parseStrictNumber(item.final_amount);
    if (Number.isNaN(amount)) {
      toast.error('Invalid amount.', { duration: 4000 });
      return;
    }
    if (amount < 0) {
      toast.error('Final amount cannot be negative.', { duration: 4000 });
      return;
    }

    const nextTotal =
      getCptSum(
        cptItems.map((row) =>
          isSameCptItem(row, item) ? { ...row, final_amount: amount } : row
        )
      ) + physicianTotal;

    if (isExceedingLoa(nextTotal)) {
      toast.error('Total amount cannot exceed LOA amount.', { duration: 4000 });
      return;
    }

    try {
      const persistedRate = toPersistedCptRate(item, amount);

      await axios.post(
        `${BASE_URL}/claims/processing/cpt/update`,
        {
          cpt_code: item.cpt_code || null,
          provider_cpt_code: item.provider_cpt_code,
          cpt_pkg_code: item.cpt_pkg_code || null,
          cpt_rate: persistedRate,
          trans_no: details.trans_no,
          loa_no: details.loa_no,
          provider_code: details.provider_code,
          claim_amount: nextTotal,
          update_by: localStorage.getItem('authentication/username') || 'system_user',
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      setCptItems((prev) =>
        prev.map((row) =>
          isSameCptItem(row, item)
            ? {
                ...row,
                cpt_rate: persistedRate,
                claims_cpt_amt: persistedRate,
                final_amount: amount,
              }
            : row
        )
      );
      toast.success('CPT updated.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to update CPT.'), { duration: 4000 });
    }
  };

  const handleRemoveCpt = async (item) => {
    if (cptItems.length <= 1) {
      toast.error('Cannot delete only remaining CPT!', { duration: 4000 });
      return;
    }

    const confirmDelete = window.confirm('Remove this CPT row?');
    if (!confirmDelete) return;

    if (item.isNew) {
      setCptItems((prev) => prev.filter((row) => row.id !== item.id));
      return;
    }

    try {
      const deleteIdentity = item.cpt_code || item.cpt_pkg_code || item.provider_cpt_code || '';
      if (!deleteIdentity) {
        toast.error('Cannot remove CPT row because no CPT identifier was found.', { duration: 4000 });
        return;
      }

      const deleteCode = encodeURIComponent(deleteIdentity);
      await axios.delete(`${BASE_URL}/claims/processing/cpt/${deleteCode}`, {
        data: {
          loa_no: details?.loa_no,
          trans_no: details?.trans_no,
          provider_cpt_code: item.provider_cpt_code,
          cpt_pkg_code: item.cpt_pkg_code || null,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });

      setCptItems((prev) => prev.filter((row) => !isSameCptItem(row, item)));
      toast.success('CPT removed.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to remove CPT.'), { duration: 4000 });
    }
  };

  const handleSelectPhysician = (option) => {
    setSelectedPhysician(option);
    setPhysicianSearch(getPhysicianDisplayName(option));
    setSpecialization(option.specialization || '');
    setShowPhysicianOptions(false);
  };

  const handleAddPhysician = async () => {
    if (!details?.trans_no || !details?.loa_no) {
      toast.error('Missing transaction or LOA number.', { duration: 4000 });
      return;
    }
    if (isFlowType1Claim && physicians.length >= FLOW_TYPE_1_MAX_PHYSICIANS) {
      toast.error('Flow Type 1 consultation allows up to 2 physicians.', { duration: 4000 });
      return;
    }
    if (!physicianSearch.trim()) {
      toast.error('Physician name is required.', { duration: 4000 });
      return;
    }
    if (professionalFee === '' || professionalFee === null || professionalFee === undefined) {
      if (!isFlowType1Claim) {
        toast.error('Professional fee is required.', { duration: 4000 });
        return;
      }
    }
    let fee = hasAmountValue(professionalFee)
      ? parseStrictNumber(professionalFee)
      : FLOW_TYPE_1_DEFAULT_PROFESSIONAL_FEE;
    if (Number.isNaN(fee)) {
      toast.error('Invalid professional fee.', { duration: 4000 });
      return;
    }
    if (fee < 0) {
      toast.error('Professional fee cannot be negative.', { duration: 4000 });
      return;
    }
    if (isFlowType1Claim && fee <= 0) {
      fee = FLOW_TYPE_1_DEFAULT_PROFESSIONAL_FEE;
    }

    const prospectiveTotal = cptTotal + physicianTotal + fee;
    if (isExceedingLoa(prospectiveTotal)) {
      toast.error('Total amount cannot exceed LOA amount.', { duration: 4000 });
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/claims/processing/physicians`,
        {
          loa_no: details.loa_no,
          trans_no: details.trans_no,
          physician_name: physicianSearch,
          physician_id: selectedPhysician?.physician_id || 'N/A',
          specialization: specialization || '',
          amount: fee,
          claim_amount: prospectiveTotal,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      const newPhysician = {
        tbl_id: response.data?.tbl_id,
        loa_no: details.loa_no,
        trans_no: details.trans_no,
        physician_name: physicianSearch,
        physician_id: selectedPhysician?.physician_id || 'N/A',
        specialization: specialization || '',
        amount: fee,
      };
      setPhysicians((prev) => [newPhysician, ...prev]);
      setPhysicianSearch('');
      setSelectedPhysician(null);
      setSpecialization('');
      setProfessionalFee('');
      toast.success('Physician added.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to add physician.'), { duration: 4000 });
    }
  };

  const handleRemovePhysician = async (physician) => {
    if (!physician?.tbl_id || !details?.trans_no) return;
    const confirmDelete = window.confirm('Remove this physician?');
    if (!confirmDelete) return;

    const physicianAmountForClaim =
      isFlowType1Claim && parseNumber(physician.amount) <= 0
        ? FLOW_TYPE_1_DEFAULT_PROFESSIONAL_FEE
        : parseNumber(physician.amount);
    const nextTotal = Math.max(computedTotalAmount - physicianAmountForClaim, 0);
    try {
      await axios.delete(`${BASE_URL}/claims/processing/physicians/${physician.tbl_id}`, {
        data: {
          trans_no: details.trans_no,
          claim_amount: nextTotal,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });

      setPhysicians((prev) => prev.filter((item) => item.tbl_id !== physician.tbl_id));
      toast.success('Physician removed.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to remove physician.'), { duration: 4000 });
    }
  };

  const handleFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(`File ${file.name} is not supported.`, { duration: 4000 });
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(`File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`, {
          duration: 4000,
        });
        return false;
      }

      return true;
    });

    if (uploadQueue.length + validFiles.length > maxUploadLimit) {
      toast.error(`You can only upload up to ${maxUploadLimit} files at a time.`, {
        duration: 4000,
      });
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      error: null,
    }));

    setUploadQueue((prev) => [...prev, ...newFiles]);
    newFiles.forEach((fileObj) => handleUploadAttachment(fileObj));
  };

  const handleUploadAttachment = async (fileObj) => {
    if (!details?.trans_no) return;
    setUploadQueue((prev) =>
      prev.map((item) => (item.file === fileObj.file ? { ...item, isUploading: true } : item))
    );

    try {
      const formData = new FormData();
      formData.append('file', fileObj.file);
      formData.append('trans_no', details.trans_no);

      const response = await axios.post(`${BASE_URL}/claims/processing/attach-files`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadQueue((prev) =>
            prev.map((item) => (item.file === fileObj.file ? { ...item, progress } : item))
          );
        },
      });

      const uploadedFile = response.data?.files?.[0];
      if (uploadedFile) {
        setAttachments((prev) => [
          {
            upload_id: uploadedFile.upload_id,
            original_file_name: uploadedFile.original_file_name,
            file_size: fileObj.file.size,
            signed_url: uploadedFile.signed_url,
            url_expires_at: uploadedFile.expires_at,
          },
          ...prev,
        ]);
      }

      setUploadQueue((prev) => prev.filter((item) => item.file !== fileObj.file));
      toast.success(`File ${fileObj.file.name} uploaded successfully.`, { duration: 4000 });
    } catch (error) {
      const errorMessage = resolveApiErrorMessage(error, 'Failed to upload file.');
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.file === fileObj.file ? { ...item, isUploading: false, error: errorMessage } : item
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  const handleRemoveAttachment = async (fileObj) => {
    if (!fileObj?.upload_id) return;
    const confirmed = window.confirm('Remove this attachment?');
    if (!confirmed) return;

    try {
      await axios.delete(`${BASE_URL}/claims/processing/attachments/${fileObj.upload_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setAttachments((prev) => prev.filter((item) => item.upload_id !== fileObj.upload_id));
      toast.success('Attachment removed.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to remove attachment.'), { duration: 4000 });
    }
  };

  const handleRefreshLink = async (fileObj, scope) => {
    if (!fileObj?.upload_id) {
      toast.error('Missing upload ID for refresh.', { duration: 4000 });
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/claims/refresh-url`,
        { upload_id: fileObj.upload_id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      const refreshedUrl =
        response.data?.signed_url || response.data?.file_url || response.data?.file?.signed_url;
      if (!refreshedUrl) {
        toast.error('Failed to refresh file link.', { duration: 4000 });
        return;
      }

      if (scope === 'soa') {
        setSoaFiles((prev) =>
          prev.map((item) =>
            item.upload_id === fileObj.upload_id ? { ...item, signed_url: refreshedUrl } : item
          )
        );
      } else if (scope === 'rtp') {
        setRtpAttachments((prev) =>
          prev.map((item) =>
            item.upload_id === fileObj.upload_id ? { ...item, signed_url: refreshedUrl } : item
          )
        );
      } else {
        setAttachments((prev) =>
          prev.map((item) =>
            item.upload_id === fileObj.upload_id ? { ...item, signed_url: refreshedUrl } : item
          )
        );
      }

      toast.success('Link refreshed successfully.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to refresh file link.'), { duration: 4000 });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileChange(event.dataTransfer.files);
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleViewFile = (fileObj) => {
    if (!fileObj?.signed_url) {
      toast.error('File URL not available.', { duration: 4000 });
      return;
    }
    window.open(fileObj.signed_url, '_blank');
  };

  const handleRtpFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(`File ${file.name} is not supported.`, { duration: 4000 });
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(`File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`, {
          duration: 4000,
        });
        return false;
      }

      return true;
    });

    const existingCount = rtpUploadFiles.length + rtpUploadQueue.length + rtpAttachments.length;
    if (existingCount + validFiles.length > 2) {
      toast.error('You can only upload up to 2 RTP attachment files.', { duration: 4000 });
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      error: null,
    }));

    setRtpUploadQueue((prev) => [...prev, ...newFiles]);
    newFiles.forEach((fileObj) => handleUploadRtpAttachment(fileObj));
  };

  const handleUploadRtpAttachment = async (fileObj) => {
    if (!details?.trans_no) return;
    setRtpUploadQueue((prev) =>
      prev.map((item) => (item.file === fileObj.file ? { ...item, isUploading: true } : item))
    );

    try {
      const formData = new FormData();
      formData.append('file', fileObj.file);
      formData.append('trans_no', details.trans_no);

      const response = await axios.post(
        `${BASE_URL}/claims/processing/rtp/attach-files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setRtpUploadQueue((prev) =>
              prev.map((item) => (item.file === fileObj.file ? { ...item, progress } : item))
            );
          },
        }
      );

      const uploadedFile = response.data?.files?.[0];
      if (uploadedFile) {
        setRtpUploadFiles((prev) => [
          {
            upload_id: uploadedFile.upload_id,
            original_file_name: uploadedFile.original_file_name,
            file_size: fileObj.file.size,
            signed_url: uploadedFile.signed_url,
            url_expires_at: uploadedFile.expires_at,
          },
          ...prev,
        ]);
      }

      setRtpUploadQueue((prev) => prev.filter((item) => item.file !== fileObj.file));
      toast.success(`File ${fileObj.file.name} uploaded successfully.`, { duration: 4000 });
    } catch (error) {
      const errorMessage = resolveApiErrorMessage(error, 'Failed to upload file.');
      setRtpUploadQueue((prev) =>
        prev.map((item) =>
          item.file === fileObj.file ? { ...item, isUploading: false, error: errorMessage } : item
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  const handleRemoveRtpAttachment = async (fileObj) => {
    if (!fileObj?.upload_id) return;
    const confirmed = window.confirm('Remove this attachment?');
    if (!confirmed) return;

    try {
      await axios.delete(`${BASE_URL}/claims/processing/rtp/attachments/${fileObj.upload_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setRtpUploadFiles((prev) => prev.filter((item) => item.upload_id !== fileObj.upload_id));
      toast.success('Attachment removed.', { duration: 3000 });
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to remove attachment.'), { duration: 4000 });
    }
  };

  const handleSubmitReturnToProvider = async () => {
    if (!details?.trans_no) return;
    if (!rtpModalRemarks.trim()) {
      toast.error('Remarks are required.', { duration: 4000 });
      return;
    }

    setIsSubmittingRtp(true);
    try {
      await axios.post(
        `${BASE_URL}/claims/processing/rtp/submit`,
        {
          trans_no: details.trans_no,
          batch_no: details.batch_no,
          remarks: rtpModalRemarks,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );
      toast.success('Claim returned successfully.', { duration: 4000 });
      setIsRtpModalOpen(false);
      handleClose();
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to return claim.'), { duration: 4000 });
    } finally {
      setIsSubmittingRtp(false);
    }
  };

  const handleUpdateClaimRemarks = async () => {
    const transNo = details?.trans_no || claim?.trans_no;
    const loaNo = details?.loa_no || claim?.loa_no;

    if (!transNo) {
      toast.error('Transaction number is required.', { duration: 4000 });
      return;
    }

    setIsUpdatingRemarks(true);
    try {
      await axios.post(
        `${BASE_URL}/claims/processing/update-remarks`,
        {
          trans_no: transNo,
          loa_no: loaNo || null,
          claim_remarks: claimRemarks,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      toast.success('Claim remarks updated successfully.', { duration: 4000 });
      await fetchClaimDetails();
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to update claim remarks.'), {
        duration: 4000,
      });
    } finally {
      setIsUpdatingRemarks(false);
    }
  };

  const handleViewTrackerRemarks = async () => {
    const params = {};
    const transNo = details?.trans_no || claim?.trans_no;
    const loaNo = details?.loa_no || claim?.loa_no;

    if (transNo) params.trans_no = transNo;
    if (loaNo) params.loa_no = loaNo;

    if (!params.trans_no && !params.loa_no) {
      toast.error('Transaction number or LOA number is required.', { duration: 4000 });
      return;
    }

    setIsTrackerRemarksModalOpen(true);
    setIsLoadingTrackerRemarks(true);
    setTrackerRemarksRows([]);

    try {
      const response = await axios.get(`${BASE_URL}/claims/processing/tracker-remarks`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setTrackerRemarksRows(response.data?.data || []);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to load tracker remarks.'), {
        duration: 4000,
      });
      setTrackerRemarksRows([]);
    } finally {
      setIsLoadingTrackerRemarks(false);
    }
  };

  const handleViewProviderPaymentSetup = async () => {
    const providerCode = details?.provider_code || claim?.provider_code;
    const transNo = details?.trans_no || claim?.trans_no;
    const params = {};

    if (providerCode) {
      params.provider_code = providerCode;
    } else if (transNo) {
      params.trans_no = transNo;
    }

    if (!params.provider_code && !params.trans_no) {
      toast.error('Provider code or transaction number is required.', { duration: 4000 });
      return;
    }

    setIsProviderPaymentSetupModalOpen(true);
    setIsLoadingProviderPaymentSetup(true);
    setProviderPaymentSetupRows([]);
    setProviderPaymentSetupDisplayRows([]);
    setProviderPaymentSetupProvider(null);

    try {
      const response = await axios.get(`${BASE_URL}/claims/processing/provider-payment-setup`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });

      setProviderPaymentSetupRows(response.data?.data || []);
      setProviderPaymentSetupDisplayRows(response.data?.display || []);
      setProviderPaymentSetupProvider(response.data?.provider || null);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to load provider payment setup.'), {
        duration: 4000,
      });
      setProviderPaymentSetupRows([]);
      setProviderPaymentSetupDisplayRows([]);
      setProviderPaymentSetupProvider(null);
    } finally {
      setIsLoadingProviderPaymentSetup(false);
    }
  };

  if (!isFullPage && !isOpen) return null;

  const handleOpenFullPage = () => {
    const params = new URLSearchParams({
      batch_no: claim?.batch_no || details?.batch_no || '',
      trn_no: claim?.trans_no || details?.trans_no || '',
      loa_no: claim?.loa_no || details?.loa_no || '',
      source_tab: sourceTab || '',
      mode,
    });
    window.open(`/claims/processing/for-processing-view?${params.toString()}`, '_blank');
  };

  const buildSavePayload = (options = {}) => {
    const transmittalValue = hasTransmittalOption ? (normalizedTransmittal === '0' ? 0 : 1) : 2;
    const claimStatus =
      details?.trans_flag_status === '03' ? 'Paid' : 'For Processing';

    return {
      trans_no: details?.trans_no || claim?.trans_no,
      tbl_id: details?.tbl_id,
      loa_no: details?.loa_no,
      batch_no: details?.batch_no,
      biller_reference: billerReference,
      diagnosis,
      diagnosis_remarks: diagnosisRemarks,
      claim_remarks: claimRemarks,
      rtp_code: rtpReason,
      rtp_remarks: rtpRemarks,
      rtp_tag: rtpEnabled ? 1 : 0,
      claim_status: claimStatus,
      claim_processor: localStorage.getItem('authentication/username') || '',
      claim_amount: totalAmount,
      transmittal: transmittalValue,
      hmc_vat_tag: isHmcVatApplicable && hmcVatTag !== '' ? Number(hmcVatTag) : null,
      cpt_rows: cptItems.map((item) => ({
        cpt_code: item.cpt_code || null,
        provider_cpt_code: item.provider_cpt_code,
        cpt_rate: toPersistedCptRate(item, parseStrictNumber(item.final_amount)),
        cpt_pkg_code: item.cpt_pkg_code || null,
      })),
      icd_code: selectedIcdId,
      override_cancelled: Boolean(options.overrideCancelled),
      override_remarks: options.overrideRemarks || '',
    };
  };

  const validateHmcVatRequirement = () => {
    if (!isHmcVatApplicable) return true;
    if (hmcVatTag !== '1' && hmcVatTag !== '0') {
      toast.error('Please select either With VAT in Billing Invoice or No VAT in Billing Invoice.', {
        duration: 4000,
      });
      return false;
    }

    return true;
  };

  const findSelectedIcdRow = () => {
    if (!selectedIcdId) return null;
    return [...icdItems, ...icdOptions].find(
      (item) => String(item?.tbl_id) === String(selectedIcdId)
    );
  };

  const validateSelectedIcdRequirement = () => {
    if (!diagnosis.trim()) {
      toast.error('Diagnosis is required.', { duration: 4000 });
      return false;
    }

    if (!selectedIcdId) {
      toast.error('Please select an ICD from the ICD search list. Free-text diagnosis is not allowed.', {
        duration: 4000,
      });
      return false;
    }

    const selectedIcd = findSelectedIcdRow();
    if (selectedIcd && Number(selectedIcd.active ?? 1) !== 1) {
      toast.error('Selected ICD is inactive. Please select an active ICD.', { duration: 4000 });
      return false;
    }

    if (String(selectedIcd?.icd_10_cm || '').trim().toUpperCase() === 'CONSULTATION') {
      toast.error('CONSULTATION is not allowed as the selected ICD. Please select a more specific ICD.', {
        duration: 4000,
      });
      return false;
    }

    return true;
  };

  const validateCptFinalAmounts = () => {
    for (const item of cptItems) {
      const code = getCptDisplayCode(item);
      if (!hasAmountValue(item.final_amount)) {
        toast.error(`Final amount is required for ${code}.`, { duration: 4000 });
        return false;
      }

      const amount = parseStrictNumber(item.final_amount);
      if (Number.isNaN(amount)) {
        toast.error(`Invalid final amount for ${code}.`, { duration: 4000 });
        return false;
      }

      if (amount < 0) {
        toast.error(`Final amount cannot be negative for ${code}.`, { duration: 4000 });
        return false;
      }
    }

    return true;
  };

  const validatePhysicians = (actionLabel) => {
    if (isClinicSettingClaim && physicians.length === 0) {
      toast.error(`Must contain at least 1 physician to ${actionLabel} this OUTPATIENT-CLINIC-SETTING claim. A zero professional fee is allowed.`, {
        duration: 5000,
      });
      return false;
    }

    if (!isFlowType1Claim) return true;

    if (physicians.length === 0) {
      toast.error(`Must contain at least 1 physician to ${actionLabel} this Flow Type 1 claim.`, {
        duration: 4000,
      });
      return false;
    }

    if (physicians.length > FLOW_TYPE_1_MAX_PHYSICIANS) {
      toast.error(`Flow Type 1 claims allow up to 2 physicians. Remove extra physicians before ${actionLabel}.`, {
        duration: 5000,
      });
      return false;
    }

    return true;
  };

  const handleOpenSaveSummary = async (options = {}) => {
    if (!billerReference.trim()) {
      toast.error('Biller Reference is required.', { duration: 4000 });
      return;
    }
    if (!validateSelectedIcdRequirement()) {
      return;
    }
    if (!validateHmcVatRequirement()) {
      return;
    }
    if (!validateCptFinalAmounts()) {
      return;
    }
    if (!validatePhysicians('save')) {
      return;
    }

    if (
      isBatchProcessingMode &&
      rtpEnabled &&
      !options.skipBatchRtpSaveConfirmation
    ) {
      setIsBatchRtpSaveConfirmOpen(true);
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildSavePayload();
      const response = await axios.post(
        `${BASE_URL}${saveEndpoint}?summary=1`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );
      setSaveSummary(response.data?.summary || null);
      setSaveSteps(response.data?.steps || []);
      setIsSaveSummaryOpen(true);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to generate summary.'), {
        duration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmBatchRtpSave = async () => {
    setIsBatchRtpSaveConfirmOpen(false);
    await handleOpenSaveSummary({ skipBatchRtpSaveConfirmation: true });
  };

  const handleConfirmSave = async () => {
    if (!validateSelectedIcdRequirement()) return;
    if (!validateCptFinalAmounts()) return;
    if (!validatePhysicians('save')) return;

    setIsSaving(true);
    try {
      const payload = buildSavePayload();
      const response = await axios.post(`${BASE_URL}${saveEndpoint}`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      toast.success('Claims saved successfully.', { duration: 4000 });
      setIsSaveSummaryOpen(false);
      if (typeof onSaved === 'function') {
        await onSaved(response.data);
      }
      await fetchClaimDetails();
      if (!isFullPage) handleClose();
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to save claim.'), {
        duration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validateLaboratoryFinalAmount = () => {
    const isLaboratoryClaim = ['OUTPATIENT-LABORATORY', 'MATERNITY-LABORATORY'].includes(
      normalizedCoverageCode
    );
    if (isLaboratoryClaim && (!Number.isFinite(cptTotal) || cptTotal < 1)) {
      toast.error(
        'Total Final Amount must be at least PHP 1.00 before processing this ' + normalizedCoverageCode + ' claim.',
        { duration: 4000 }
      );
      return false;
    }

    return true;
  };

  const validateProcessRequirements = () => {
    if (!validateLaboratoryFinalAmount()) return false;
    if (!billerReference.trim()) {
      toast.error('Biller Reference is required.', { duration: 4000 });
      return false;
    }
    if (!validateSelectedIcdRequirement()) {
      return false;
    }
    if (!validateHmcVatRequirement()) {
      return false;
    }
    if (!validateCptFinalAmounts()) {
      return false;
    }
    if (!validatePhysicians('process')) {
      return false;
    }

    return true;
  };

  const requestProcessSummary = async () => {
    if (!validateLaboratoryFinalAmount()) return;
    if (!validateSelectedIcdRequirement()) return;
    if (!validatePhysicians('process')) return;

    setIsProcessing(true);
    try {
      const payload = buildSavePayload({
        overrideCancelled: shouldOverrideCancelled,
        overrideRemarks,
      });
      const response = await axios.post(
        `${BASE_URL}/claims/processing/process?summary=1`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );
      setProcessSummary(response.data?.summary || null);
      setProcessSteps(response.data?.steps || []);
      setIsProcessSummaryOpen(true);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to generate process summary.'), {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenProcessSummary = async () => {
    if (!validateProcessRequirements()) return;

    if (details?.trans_flag_status === '04' && !shouldOverrideCancelled) {
      setIsOverrideModalOpen(true);
      return;
    }

    if (hasTransmittalOption) {
      setIsTransmittalConfirmOpen(true);
      return;
    }
    await requestProcessSummary();
  };

  const continueAfterOverrideApproval = async () => {
    if (hasTransmittalOption) {
      setIsTransmittalConfirmOpen(true);
      return;
    }
    await requestProcessSummary();
  };

  const handleConfirmCancelledOverride = async () => {
    if (!overrideRemarks.trim()) {
      toast.error('Override remarks are required before you can override this cancelled claim.', {
        duration: 4000,
      });
      return;
    }

    setShouldOverrideCancelled(true);
    setIsOverrideModalOpen(false);
    await continueAfterOverrideApproval();
  };

  const handleConfirmTransmittalSelection = async () => {
    setIsTransmittalConfirmOpen(false);
    await requestProcessSummary();
  };

  const handleConfirmProcess = async () => {
    if (!validateLaboratoryFinalAmount()) return;
    if (!validateSelectedIcdRequirement()) return;
    if (!validateCptFinalAmounts()) return;
    if (!validatePhysicians('process')) return;

    setIsProcessing(true);
    try {
      const payload = buildSavePayload({
        overrideCancelled: shouldOverrideCancelled,
        overrideRemarks,
      });
      await axios.post(`${BASE_URL}/claims/processing/process`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      toast.success('Claims processed successfully.', { duration: 4000 });
      setIsProcessSummaryOpen(false);
      handleClose();
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to process claim.'), {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderClaimAmountConfirmation = (summary) => {
    const basis = summary?.claim_amount_basis || {};
    const flowRule = summary?.flow_type_1_physician_rule || {};
    const cptFinalAmountTotal = hasAmountValue(basis.cpt_final_amount_total)
      ? parseNumber(basis.cpt_final_amount_total)
      : cptTotal;
    const professionalFeeTotal = hasAmountValue(basis.professional_fee_total)
      ? parseNumber(basis.professional_fee_total)
      : physicianTotal;
    const defaultApplied =
      Boolean(flowRule.default_applied) || flowType1DefaultFeePhysicians > 0;

    return (
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">
          Claim Amount Confirmation
        </h4>
        <p className="text-blue-800">
          Final Amount values in the CPT List will be saved as Claims CPT Amt.
          Claim Amount will use the displayed Final Amount total plus Professional Fee total.
        </p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <span className="text-blue-700">CPT Final Amount Total</span>
            <div className="font-semibold text-blue-950">
              {phpFormatter.format(cptFinalAmountTotal)}
            </div>
          </div>
          <div>
            <span className="text-blue-700">Professional Fee Total</span>
            <div className="font-semibold text-blue-950">
              {phpFormatter.format(professionalFeeTotal)}
            </div>
          </div>
          <div>
            <span className="text-blue-700">Claim Amount</span>
            <div className="font-semibold text-blue-950">
              {phpFormatter.format(parseNumber(summary?.claim_amount ?? totalAmount))}
            </div>
          </div>
        </div>
        {isFlowType1Claim && (
          <div className="mt-3 rounded-lg bg-white/70 border border-blue-100 px-3 py-2 text-blue-800">
            Flow Type 1 allows 1 to 2 physicians. Blank or zero professional fee
            {defaultApplied ? ' was defaulted' : ' will default'} to{' '}
            {phpFormatter.format(FLOW_TYPE_1_DEFAULT_PROFESSIONAL_FEE)}.
          </div>
        )}
      </div>
    );
  };

  const actionButtons = isReadOnly ? (
    <>
      <button
        type="button"
        onClick={handleClose}
        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
      >
        <FontAwesomeIcon icon={faTimes} />
        Close
      </button>
    </>
  ) : isBatchProcessingMode ? (
    <>
      <button
        type="button"
        onClick={handleOpenSaveSummary}
        disabled={isSaving || isProcessing}
        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
      >
        <FontAwesomeIcon icon={faSave} />
        {isSaving ? 'Processing...' : 'Save'}
      </button>
      <button
        type="button"
        onClick={handleClose}
        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
      >
        <FontAwesomeIcon icon={faTimes} />
        Close
      </button>
    </>
  ) : (
    <>
      <button
        type="button"
        onClick={handleOpenSaveSummary}
        disabled={isSaving || isProcessing}
        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
      >
        <FontAwesomeIcon icon={faSave} />
        {isSaving ? 'Processing...' : 'Save'}
      </button>
      <button
        type="button"
        onClick={handleOpenProcessSummary}
        disabled={isSaving || isProcessing}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        <FontAwesomeIcon icon={faClipboardCheck} />
        Proceed & Confirm
      </button>
      <button
        type="button"
        onClick={() => setIsRtpModalOpen(true)}
        disabled={isSaving || isProcessing}
        className={`inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 ${
          showReturnButton ? '' : 'hidden'
        }`}
      >
        <FontAwesomeIcon icon={faUndo} />
        Return To Provider
      </button>
      <button
        type="button"
        onClick={handleClose}
        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
      >
        <FontAwesomeIcon icon={faTimes} />
        Close
      </button>
    </>
  );

  const trnNoDisplay = String(details?.trans_no || claim?.trans_no || '-').trim() || '-';
  const loaNoDisplay = String(details?.loa_no || claim?.loa_no || '-').trim() || '-';
  const claimStatusMeta = getClaimStatusMeta(
    details?.claim_status || claim?.claim_status,
    details?.rtp_tag ?? claim?.rtp_tag
  );

  return (
    <div className={isFullPage
      ? 'min-h-screen bg-slate-100'
      : `fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${isMaximized ? 'p-0' : ''}`
    }>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div
        className={isFullPage
          ? 'bg-white w-full min-h-screen flex flex-col'
          : `bg-white shadow-[0_20px_60px_rgba(15,23,42,0.35)] w-full overflow-hidden flex flex-col transition-all duration-300 ease-in-out ${
              isMaximized
                ? 'h-screen max-w-full max-h-full rounded-none'
                : 'max-w-7xl max-h-[94vh] rounded-2xl'
            }`
        }
      >
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              {`Process Claim - TRN No: ${trnNoDisplay} - Loa No: ${loaNoDisplay}`}
            </h2>
            <p className="text-sm text-slate-500">
              Review and update claim details before processing.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                TRN No: {trnNoDisplay}
              </span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                Loa No: {loaNoDisplay}
              </span>
              {isBatchProcessingMode && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  Batch For Processing
                </span>
              )}
            </div>
            {details?.trans_flag_status && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-slate-500">TRN Status:</span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                    details.trans_flag_status === '04'
                      ? 'bg-red-100 text-red-700'
                      : details.trans_flag_status === '03'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  [{details.trans_flag_status}] {details.trans_flag_desc || details.trans_flag_status}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-500">Claim Status:</span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${claimStatusMeta.className}`}
              >
                {claimStatusMeta.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isFullPage && (
              <button
                type="button"
                onClick={handleOpenFullPage}
                className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 border border-indigo-200 transition-colors"
                title="Open in a new full page tab"
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                View in Full Page
                <span className="absolute -top-2 -right-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none shadow-sm">
                  New
                </span>
              </button>
            )}
            {!isFullPage && (
              <button
                type="button"
                onClick={() => setIsMaximized((prev) => !prev)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                title={isMaximized ? 'Restore to original size' : 'Maximize to full screen'}
              >
                <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
                {isMaximized ? 'Restore' : 'Maximize'}
              </button>
            )}
            {!isFullPage && (
              <button
                onClick={handleClose}
                className="text-slate-500 hover:text-slate-700 transition-colors"
                aria-label="Close modal"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>

        {/* <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap justify-end gap-3 bg-white">
          {actionButtons}
        </div> */}

        {details?.trans_flag_status === '04' && (
          <div className="mx-6 mt-3 px-4 py-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-red-700 font-semibold text-sm">&#9888; Cancelled Transaction</span>
            </div>
            {details.cancelled_info?.cancelled_reason && (
              <p className="text-red-600 text-xs"><strong>Reason:</strong> {details.cancelled_info.cancelled_reason}</p>
            )}
            {details.cancelled_info?.cancelled_by && (
              <p className="text-red-500 text-xs"><strong>By:</strong> {details.cancelled_info.cancelled_by}</p>
            )}
            {details.cancelled_info?.cancelled_date && (
              <p className="text-red-500 text-xs"><strong>Date:</strong> {details.cancelled_info.cancelled_date}</p>
            )}
            <p className="text-red-500 text-xs mt-1 italic">Note: Attempting to process this claim will be rejected by the system.</p>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} className="text-2xl text-emerald-600 animate-spin" />
            </div>
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 shadow-md">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <h3 className="text-sm font-semibold text-slate-700">Claim Header</h3>
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={handleViewProviderPaymentSetup}
                            disabled={isLoadingProviderPaymentSetup}
                            className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 rounded-lg border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50"
                            title="View Provider Payment Set-up"
                          >
                            <FontAwesomeIcon icon={isLoadingProviderPaymentSetup ? faSpinner : faEye} className={isLoadingProviderPaymentSetup ? 'animate-spin' : ''} />
                            <span>
                              {isLoadingProviderPaymentSetup
                                ? 'Loading Set-up...'
                                : 'View Provider Payment Set-up'}
                            </span>
                          </button>
                          <span className="absolute -top-2 -right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none shadow-sm">
                            New
                          </span>
                        </div>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowClaimHeaderDetails(!showClaimHeaderDetails)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-white rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
                            title={showClaimHeaderDetails ? "Hide Details" : "Show Details"}
                          >
                            <FontAwesomeIcon icon={showClaimHeaderDetails ? faChevronUp : faChevronDown} />
                            <span>{showClaimHeaderDetails ? 'Hide' : 'Show'} Details</span>
                          </button>
                          <span className="absolute -top-2 -right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none shadow-sm">
                            New
                          </span>
                        </div>
                      </div>
                    </div>
                    {showClaimHeaderDetails && (
                      <div className="space-y-2 text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Batch No</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.batch_no || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Patient</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.patient_name || details?.member_name || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Corporate</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.corp_name || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Plan Code</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.plan_code || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Coverage</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.coverage_code || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Provider</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.provider_name || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Admission Date</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.admission_date || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">TRN No</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.trans_no || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">LOA No</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.loa_no || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">SOA No</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {details?.soa_no || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Provider CC Email</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {providerContact?.email_add || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Provider Fax</span>
                        <span className="font-medium text-slate-700 text-right break-words max-w-[60%]">
                          {providerContact?.fax_no || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">TRN Status</span>
                        <span
                          className={`font-medium text-right break-words max-w-[60%] ${
                            details?.trans_flag_status === '04' ? 'text-red-600' : 'text-slate-700'
                          }`}
                        >
                          {details?.trans_flag_desc
                            ? `[${details.trans_flag_status}] ${details.trans_flag_desc}`
                            : (details?.trans_flag_status || '-')}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Claim Status</span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${claimStatusMeta.className}`}
                        >
                          {claimStatusMeta.label}
                        </span>
                      </div>
                    </div>
                    )}
                  </div>
                  <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-md">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Editable Details</h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <label className="text-slate-500">Chief Complaint</label>
                        <input
                          type="text"
                          value={chiefComplaint}
                          onChange={(e) => setChiefComplaint(e.target.value)}
                          disabled={isReadOnly}
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                        />
                      </div>
                      <div className="relative">
                        <label className="text-slate-500">
                          Diagnosis <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={diagnosis}
                          required
                          onChange={(e) => {
                            setDiagnosis(e.target.value);
                            setSelectedIcdId(null);
                            setIcdSearchTerm(e.target.value);
                            setShowIcdOptions(true);
                          }}
                          onFocus={() => setShowIcdOptions(true)}
                          disabled={isReadOnly}
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                        />
                        {showIcdOptions && icdOptions.length > 0 && (
                          <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg mt-1 max-h-48 overflow-y-auto">
                            {icdOptions.map((option) => (
                              <button
                                key={option.tbl_id}
                                type="button"
                                onClick={() => {
                                  const value =
                                    option.condition || option.icd_10_cm || option.icd_9_cm || '';
                                  if (String(option.icd_10_cm || '').trim().toUpperCase() === 'CONSULTATION') {
                                    toast.error('CONSULTATION is not allowed. Please select a more specific ICD.', {
                                      duration: 4000,
                                    });
                                    return;
                                  }
                                  if (Number(option.active ?? 1) !== 1) {
                                    toast.error('Selected ICD is inactive. Please select an active ICD.', {
                                      duration: 4000,
                                    });
                                    return;
                                  }
                                  setDiagnosis(value);
                                  setSelectedIcdId(option.tbl_id);
                                  setIcdSearchTerm('');
                                  setShowIcdOptions(false);
                                }}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50"
                              >
                                <div className="font-semibold text-slate-700">
                                  {option.icd_10_cm || option.icd_9_cm}
                                </div>
                                <div className="text-slate-500">{option.condition}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="text-slate-500">Diagnosis Remarks</label>
                        <input
                          type="text"
                          value={diagnosisRemarks}
                          onChange={(e) => setDiagnosisRemarks(e.target.value)}
                          disabled={isReadOnly}
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                        />
                      </div>
                      <div>
                        <label className="text-slate-500">
                          Biller Reference <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billerReference}
                          required
                          onChange={(e) => setBillerReference(e.target.value)}
                          disabled={isReadOnly}
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                        />
                      </div>
                      <div>
                        <label className="text-slate-500">Claim Remarks</label>
                        <textarea
                          value={claimRemarks}
                          onChange={(e) => setClaimRemarks(e.target.value)}
                          rows={4}
                          disabled={isClaimRemarksReadOnly}
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                        />
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={handleViewTrackerRemarks}
                            disabled={isLoadingTrackerRemarks}
                            className="relative inline-flex items-center px-4 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 rounded-lg border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50"
                          >
                            {isLoadingTrackerRemarks ? 'Loading Tracker Remarks...' : 'View Tracker Remarks'}
                            <span className="absolute -top-2 -right-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none shadow-sm">
                              New
                            </span>
                          </button>
                          {canUpdateRemarksInView && (
                            <button
                              type="button"
                              onClick={handleUpdateClaimRemarks}
                              disabled={isUpdatingRemarks || loading}
                              className="relative inline-flex items-center px-4 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {isUpdatingRemarks ? 'Updating...' : 'Update Remarks'}
                              <span className="absolute -top-2 -right-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none shadow-sm">
                                New
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                      {hasTransmittalOption && (
                        <div>
                          <label className="text-slate-500">Transmittal</label>
                          {isReadOnly ? (
                            <div
                              className={`mt-2 inline-flex items-center gap-3 rounded-xl border px-3 py-2 text-xs font-semibold ${
                                isPaidToDoctor
                                  ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                                  : 'border-blue-200 bg-blue-50 text-blue-700'
                              }`}
                            >
                              <span
                                className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${
                                  isPaidToDoctor
                                    ? 'bg-cyan-100 text-cyan-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                <FontAwesomeIcon icon={isPaidToDoctor ? faUserDoctor : faHospital} />
                              </span>
                              <span className="leading-tight">
                                {payoutLabel}
                                <span className="block text-[11px] font-medium opacity-80">
                                  {payoutSubLabel}
                                </span>
                              </span>
                            </div>
                          ) : (
                            <div className="mt-2 flex flex-wrap gap-6 text-xs">
                              <label className="flex items-start gap-2">
                                <input
                                  type="radio"
                                  name="transmittal"
                                  value="1"
                                  checked={transmittal === '1'}
                                  onChange={() => setTransmittal('1')}
                                />
                                <span>
                                  With Transmittal <br />
                                  <strong>(Based on Provider Set-up)</strong>
                                  <span className="block text-[11px] text-slate-500">
                                    Hospital/Clinic, Physician, or Split
                                  </span>
                                </span>
                              </label>
                              <label className="flex items-start gap-2">
                                <input
                                  type="radio"
                                  name="transmittal"
                                  value="0"
                                  checked={transmittal === '0'}
                                  onChange={() => setTransmittal('0')}
                                />
                                <span>
                                  No Transmittal <br />
                                  <strong>(Paid to Doctor)</strong>
                                </span>
                              </label>
                            </div>
                          )}
                          {!isReadOnly && (
                            <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[11px] leading-4 text-blue-700">
                              With Transmittal follows the Provider Payment Set-up. No Transmittal remains Paid to Doctor.
                            </div>
                          )}
                        </div>
                      )}
                      <div>
                        <label className="text-slate-500 inline-flex items-center gap-2 flex-wrap">
                          <span>
                            HMC VAT Billing Invoice
                            {isHmcVatApplicable && <span className="text-red-500"> *</span>}
                          </span>
                          <span className="inline-flex items-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white leading-none">
                            New
                          </span>
                        </label>
                        {isReadOnly ? (
                          <div className="mt-2 min-h-[44px] w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                            {hmcVatTagLabel}
                          </div>
                        ) : (
                          <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                            {HMC_VAT_OPTIONS.map((option) => {
                              const isChecked = hmcVatTag === option.value;
                              return (
                                <label
                                  key={option.value}
                                  className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-xs transition-colors ${
                                    isHmcVatApplicable
                                      ? isChecked
                                        ? 'border-red-300 bg-red-50 text-slate-800'
                                        : 'border-slate-200 bg-white text-slate-700'
                                      : 'border-slate-200 bg-slate-100 text-slate-400'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="hmc_vat_tag"
                                    value={option.value}
                                    checked={isChecked}
                                    disabled={!isHmcVatApplicable}
                                    onChange={() => setHmcVatTag(option.value)}
                                  />
                                  <span>{option.label}</span>
                                </label>
                              );
                            })}
                            <p className="text-[11px] text-slate-500">
                              Required only when provider name contains THE MEDICAL CITY CLINIC or HEALTHWAY.
                            </p>
                          </div>
                        )}
                      </div>
                      {isHmcVatApplicable && (
                        <VatComputationBreakdown
                          transactionAmount={displayTotalAmount}
                          ppdTag={details?.ppd_tag}
                          ppdTerm={details?.ppd_term}
                          ppdRate={details?.ppd_rate}
                          providerName={details?.provider_name}
                          hmcVatTag={hmcVatTag}
                        />
                      )}
                    </div>
                  </div>

                </div>

                <div className="lg:col-span-2 space-y-4">
                  <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-md">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <h3 className="text-sm font-semibold text-slate-700">CPT List</h3>
                      <div className="flex items-center gap-2">
                        {!isReadOnly && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleAddSpecialCpt('reader')}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                              Add Reader&apos;s Fee
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAddSpecialCpt('discount')}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                              Add Discount
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left">CPT</th>
                            <th className="px-3 py-2 text-left">Code</th>
                            <th className="px-3 py-2 text-left">Default Price</th>
                            <th className="px-3 py-2 text-left">LOA CPT Amt.</th>
                            <th className="px-3 py-2 text-left">Claims CPT Amt.</th>
                            <th className="px-3 py-2 text-left">Final Amount</th>
                            <th className="px-3 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {cptItems.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="px-3 py-4 text-center text-slate-500">
                                No CPT entries.
                              </td>
                            </tr>
                          ) : (
                            cptItems.map((item, index) => (
                              <tr key={getCptRowKey(item, index)}>
                                <td className="px-3 py-2">{item.cpt_desc || '-'}</td>
                                <td className="px-3 py-2">{getCptDisplayCode(item)}</td>
                                <td className="px-3 py-2">
                                  {phpFormatter.format(parseNumber(item.default_price))}
                                </td>
                                <td className="px-3 py-2">
                                  {formatNullableCptAmount(item, 'loa_cpt_amt')}
                                </td>
                                <td className="px-3 py-2">
                                  {formatNullableCptAmount(item, 'claims_cpt_amt')}
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    value={item.final_amount}
                                    onChange={(e) => handleFinalAmountChange(index, e.target.value)}
                                    disabled={isReadOnly}
                                    className="w-full px-2 py-1 border border-slate-200 rounded disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <div className={`flex items-center gap-2 ${isReadOnly ? 'hidden' : ''}`}>
                                    {item.isNew ? (
                                      <button
                                        type="button"
                                        onClick={() => handleAddCpt(item)}
                                        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded hover:bg-emerald-100"
                                      >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Add
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateCpt(item)}
                                        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-50 rounded hover:bg-blue-100"
                                      >
                                        <FontAwesomeIcon icon={faSyncAlt} />
                                        Update
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveCpt(item)}
                                      className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-red-700 bg-red-50 rounded hover:bg-red-100"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                      Remove
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {cptItems.length > 0 && (
                          <tfoot className="bg-slate-50 border-t border-slate-200">
                            <tr>
                              <td colSpan={2} className="px-3 py-2 font-bold text-slate-700">
                                Total
                              </td>
                              <td className="px-3 py-2 font-bold text-slate-700">
                                <div className="text-[11px] uppercase text-slate-500">
                                  Total Default Price
                                </div>
                                {phpFormatter.format(cptDefaultPriceTotal)}
                              </td>
                              <td className="px-3 py-2 font-bold text-slate-700">
                                <div className="text-[11px] uppercase text-slate-500">
                                  Total LOA CPT Amt.
                                </div>
                                {phpFormatter.format(cptLoaAmountTotal)}
                              </td>
                              <td className="px-3 py-2 font-bold text-slate-700">
                                <div className="text-[11px] uppercase text-slate-500">
                                  Total Claims CPT Amt.
                                </div>
                                {phpFormatter.format(cptClaimsAmountTotal)}
                              </td>
                              <td className="px-3 py-2 font-bold text-slate-700">
                                <div className="text-[11px] uppercase text-slate-500">
                                  Total Final Amount
                                </div>
                                {phpFormatter.format(cptTotal)}
                              </td>
                              <td className="px-3 py-2"></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                    <div className="mt-3 text-md text-slate-700 flex flex-wrap gap-6 items-center">
                      <span>
                        <span className="font-bold">LOA Amount:</span> {phpFormatter.format(loaAmount)}
                      </span>
                      <span>
                        <span className="font-bold">Total Amount:</span> {phpFormatter.format(displayTotalAmount)}
                      </span>
                      {loaAmount > 0 ? (
                        shouldEnforceLoaCap ? (
                          <span className="text-amber-600 font-medium">Cannot exceed LOA Amt</span>
                        ) : (
                          <span className="text-emerald-600 font-medium">LOA overage allowed for this coverage</span>
                        )
                      ) : (
                        <span className="text-slate-500 font-medium">No LOA amount cap configured</span>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-md">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-700">View Documents</h3>
                      <button
                        type="button"
                        onClick={() => setShowDocuments((prev) => !prev)}
                        className="text-slate-500 hover:text-slate-700"
                      >
                        <FontAwesomeIcon icon={showDocuments ? faChevronUp : faChevronDown} />
                      </button>
                    </div>
                    {showDocuments && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <h4 className="text-xs font-semibold text-slate-600">SOA Files</h4>
                          <ul className="mt-2 space-y-2 text-xs">
                            {soaFiles.length === 0 ? (
                              <li className="text-slate-500">No SOA files.</li>
                            ) : (
                              soaFiles.map((file) => (
                                <li
                                  key={file.upload_id || file.file_name}
                                  className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg"
                                >
                                  <span className="truncate">{file.original_file_name}</span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleViewFile(file)}
                                      className="text-emerald-600 hover:text-emerald-700"
                                    >
                                      <FontAwesomeIcon icon={faEye} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRefreshLink(file, 'soa')}
                                      className="text-blue-600 hover:text-blue-700"
                                    >
                                      <FontAwesomeIcon icon={faSyncAlt} />
                                    </button>
                                  </div>
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold text-slate-600">Attachments</h4>
                          <ul className="mt-2 space-y-2 text-xs">
                            {attachments.length === 0 ? (
                              <li className="text-slate-500">No attachments.</li>
                            ) : (
                              attachments.map((file) => (
                                <li
                                  key={file.upload_id}
                                  className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg"
                                >
                                  <span className="truncate">{file.original_file_name}</span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleViewFile(file)}
                                      className="text-emerald-600 hover:text-emerald-700"
                                    >
                                      <FontAwesomeIcon icon={faEye} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRefreshLink(file, 'attachment')}
                                      className="text-blue-600 hover:text-blue-700"
                                    >
                                      <FontAwesomeIcon icon={faSyncAlt} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveAttachment(file)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </div>
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold text-slate-600">Upload Attachment</h4>
                          <div
                            className={`mt-2 border-2 border-dashed rounded-xl p-4 text-center ${
                              isDragging ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'
                            }`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <FontAwesomeIcon icon={faUpload} className="text-2xl text-emerald-500" />
                            <p className="text-xs text-slate-500 mt-2">
                              Drag & drop files or{' '}
                              <button
                                type="button"
                                onClick={handleChooseFile}
                                className="text-emerald-600 hover:text-emerald-700 font-semibold"
                              >
                                browse
                              </button>
                            </p>
                            <input
                              type="file"
                              ref={fileInputRef}
                              multiple
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleFileChange(e.target.files)}
                              className="hidden"
                            />
                          </div>
                          {uploadQueue.length > 0 && (
                            <div className="mt-3 space-y-2 text-xs">
                              {uploadQueue.map((item) => (
                                <div
                                  key={item.file.name}
                                  className="bg-slate-50 rounded-lg px-3 py-2"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="truncate">{item.file.name}</span>
                                    {item.isUploading && (
                                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    )}
                                  </div>
                                  <div className="mt-2 h-2 bg-slate-200 rounded-full">
                                    <div
                                      className="h-2 bg-emerald-500 rounded-full"
                                      style={{ width: `${item.progress}%` }}
                                    />
                                  </div>
                                  {item.error && (
                                    <p className="text-red-500 mt-1">Error: {item.error}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold text-slate-600">View Other Files Reference</h4>
                          <ul className="mt-2 space-y-2 text-xs">
                            {otherFileReferences.length === 0 ? (
                              <li className="text-slate-500">No other file references.</li>
                            ) : (
                              otherFileReferences.map((file, index) => (
                                <li
                                  key={`${file.source || 'reference'}-${file.file_name || 'file'}-${index}`}
                                  className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-2 rounded-lg"
                                >
                                  <div className="min-w-0">
                                    <p className="truncate text-slate-700">{file.file_name || '-'}</p>
                                    <p className="text-[11px] text-slate-500">{file.label || file.source || 'Reference'}</p>
                                  </div>
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-semibold whitespace-nowrap"
                                  >
                                    Open
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                  </a>
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-md">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">ICD List</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left">ICD Code</th>
                            <th className="px-3 py-2 text-left">Condition</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {icdItems.length === 0 ? (
                            <tr>
                              <td colSpan={2} className="px-3 py-4 text-center text-slate-500">
                                No ICD entries.
                              </td>
                            </tr>
                          ) : (
                            icdItems.map((item) => (
                              <tr key={item.tbl_id}>
                                <td className="px-3 py-2">
                                  {item.icd_10_cm || item.icd_code || '-'}
                                </td>
                                <td className="px-3 py-2">{item.condition || '-'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-md">
                    <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <h3 className="text-sm font-semibold text-slate-700">Return To Provider?</h3>
                      <input
                        type="checkbox"
                        checked={rtpEnabled}
                        onChange={(e) => setRtpEnabled(e.target.checked)}
                        disabled={isReadOnly}
                        className="h-4 w-4"
                      />
                    </div>
                    {rtpEnabled && (
                      <div className="mt-3 space-y-3 text-sm">
                        <div>
                          <label className="text-slate-500">Reason</label>
                          <select
                            value={rtpReason}
                            onChange={(e) => setRtpReason(e.target.value)}
                            disabled={isReadOnly}
                            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                          >
                            <option value="">Select reason</option>
                            {rtpReasons.map((reason) => (
                              <option key={reason.rtp_code} value={reason.rtp_code}>
                                {reason.reasons}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-slate-500">Remarks</label>
                          <textarea
                            value={rtpRemarks}
                            onChange={(e) => setRtpRemarks(e.target.value)}
                            rows={3}
                            disabled={isReadOnly}
                            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {rtpDetails && (
                    <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-md">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">RTP Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-600">
                        <div>
                          <span className="text-slate-500">Reason</span>
                          <div className="font-semibold text-slate-700">
                            {rtpDetails.reasons || rtpDetails.rtp_code || '-'}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500">Remarks</span>
                          <div className="font-semibold text-slate-700">
                            {rtpDetails.remarks || '-'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <h4 className="text-xs font-semibold text-slate-600">RTP Attachments</h4>
                        <ul className="mt-2 space-y-2 text-xs">
                          {rtpAttachments.length === 0 ? (
                            <li className="text-slate-500">No RTP attachments.</li>
                          ) : (
                            rtpAttachments.map((file) => (
                              <li
                                key={file.upload_id}
                                className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg"
                              >
                                <span className="truncate">{file.original_file_name}</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleViewFile(file)}
                                    className="text-emerald-600 hover:text-emerald-700"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRefreshLink(file, 'rtp')}
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <FontAwesomeIcon icon={faSyncAlt} />
                                  </button>
                                </div>
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-md">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-700">
                    Physicians
                    {isClinicSettingClaim && <span className="ml-1 text-red-500">*</span>}
                  </h3>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={onlyAffiliatedPhysicians}
                      onChange={(e) => {
                        setOnlyAffiliatedPhysicians(e.target.checked);
                        setPhysicianOptions([]);
                        setShowPhysicianOptions(true);
                      }}
                      disabled={isReadOnly}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-60"
                    />
                    <span>Only Show Affiliated Physician</span>
                  </label>
                </div>
                {isClinicSettingClaim && (
                  <p className="mb-3 text-xs text-slate-600">
                    At least 1 physician is required for this coverage. A zero professional fee
                    is allowed, and you may add multiple physicians.
                  </p>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 text-sm items-end">
                  <div className="relative lg:col-span-5">
                    <label className="text-slate-500">Enter Physician</label>
                    <input
                      type="text"
                      value={physicianSearch}
                      onChange={(e) => {
                        setPhysicianSearch(e.target.value);
                        setSelectedPhysician(null);
                        setShowPhysicianOptions(true);
                      }}
                      onFocus={() => setShowPhysicianOptions(true)}
                      disabled={isReadOnly}
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                      placeholder="Search physician"
                    />
                    {showPhysicianOptions && physicianOptions.length > 0 && (
                      <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg mt-1 max-h-48 overflow-y-auto">
                        {physicianOptions.map((option) => (
                          <button
                            key={option.tbl_id || option.physician_id}
                            type="button"
                            onClick={() => handleSelectPhysician(option)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="font-semibold text-slate-700">
                                {getPhysicianDisplayName(option) || 'Unnamed Physician'}
                              </div>
                              <span
                                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  isOptionAffiliatedPhysician(option)
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {option.affiliation_status ||
                                  (isOptionAffiliatedPhysician(option)
                                    ? 'Affiliated'
                                    : 'Not Affiliated')}
                              </span>
                            </div>
                            <div className="text-slate-500">
                              {option.specialization || '-'} | {option.room_no || '-'} |{' '}
                              {option.schedule || '-'} | {option.remarks || '-'}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="lg:col-span-3">
                    <label className="text-slate-500">Specialization</label>
                    <input
                      type="text"
                      value={specialization}
                      onChange={(e) => setSpecialization(e.target.value)}
                      disabled={isReadOnly}
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <label className="text-slate-500">Prof. Fee</label>
                    <input
                      type="number"
                      value={professionalFee}
                      onChange={(e) => setProfessionalFee(e.target.value)}
                      disabled={isReadOnly}
                      placeholder={isFlowType1Claim ? '600 default' : ''}
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg disabled:bg-slate-100"
                    />
                  </div>
                  <div className="lg:col-span-2 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddPhysician}
                      disabled={isReadOnly || (isFlowType1Claim && physicians.length >= FLOW_TYPE_1_MAX_PHYSICIANS)}
                      className={`inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed w-full lg:w-auto ${
                        isReadOnly ? 'hidden' : ''
                      }`}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Add Physician
                    </button>
                  </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Physician ID</th>
                        <th className="px-3 py-2 text-left">Physician</th>
                        <th className="px-3 py-2 text-left">Specialization</th>
                        <th className="px-3 py-2 text-left">Prof. Fee</th>
                        <th className="px-3 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginatedPhysicians.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-center text-slate-500">
                            No physicians added.
                          </td>
                        </tr>
                      ) : (
                        paginatedPhysicians.map((item) => (
                          <tr key={item.tbl_id || item.physician_id}>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatPhysicianId(item.physician_id)}
                            </td>
                            <td className="px-3 py-2">{item.physician_name}</td>
                            <td className="px-3 py-2">{item.specialization || '-'}</td>
                            <td className="px-3 py-2">
                              {phpFormatter.format(
                                isFlowType1Claim && parseNumber(item.amount) <= 0
                                  ? FLOW_TYPE_1_DEFAULT_PROFESSIONAL_FEE
                                  : parseNumber(item.amount)
                              )}
                              {isFlowType1Claim && parseNumber(item.amount) <= 0 && (
                                <span className="ml-1 text-[10px] font-semibold text-amber-600">
                                  default
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => handleRemovePhysician(item)}
                                className={`text-red-600 hover:text-red-700 ${isReadOnly ? 'hidden' : ''}`}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <select
                      value={physicianPerPage}
                      onChange={(e) => {
                        setPhysicianPerPage(Number(e.target.value));
                        setPhysicianPage(1);
                      }}
                      className="px-2 py-1 border border-slate-200 rounded"
                    >
                      {[5, 10, 20, 50].map((val) => (
                        <option key={val} value={val}>
                          {val} per page
                        </option>
                      ))}
                    </select>
                    <span>{showingPhysiciansText()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPhysicianPage((prev) => Math.max(1, prev - 1))}
                      disabled={physicianPage === 1}
                      className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span>
                      {physicianPage} / {physicianTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPhysicianPage((prev) => Math.min(physicianTotalPages, prev + 1))}
                      disabled={physicianPage === physicianTotalPages}
                      className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      <div className="p-6 border-t border-slate-100 flex flex-wrap justify-end gap-3">
        {actionButtons}
      </div>
    </div>

      {isTransmittalConfirmOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Confirm Payout Destination</h3>
                <p className="text-sm text-slate-500">
                  Double-check the transmittal option and payout basis before opening the process summary.
                </p>
                <button
                  type="button"
                  onClick={handleViewProviderPaymentSetup}
                  disabled={isLoadingProviderPaymentSetup}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                >
                  <FontAwesomeIcon
                    icon={isLoadingProviderPaymentSetup ? faSpinner : faEye}
                    className={isLoadingProviderPaymentSetup ? 'animate-spin' : ''}
                  />
                  {isLoadingProviderPaymentSetup
                    ? 'Loading Set-up...'
                    : 'View Provider Payment Set-up'}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setIsTransmittalConfirmOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setTransmittal('1')}
                  className={`text-left rounded-2xl border p-4 transition-all ${
                    !isPaidToDoctor
                      ? 'border-blue-300 bg-blue-50 shadow-[0_8px_20px_rgba(37,99,235,0.18)]'
                      : 'border-slate-200 bg-white hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${
                        !isPaidToDoctor ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      <FontAwesomeIcon icon={faHospital} />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">Based on Provider Set-up</div>
                      <div className="text-xs text-slate-500">With Transmittal</div>
                      <div className="mt-1 text-xs leading-5 text-slate-500">
                        May be paid to hospital/clinic, physician, or split.
                      </div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setTransmittal('0')}
                  className={`text-left rounded-2xl border p-4 transition-all ${
                    isPaidToDoctor
                      ? 'border-cyan-300 bg-cyan-50 shadow-[0_8px_20px_rgba(6,182,212,0.18)]'
                      : 'border-slate-200 bg-white hover:border-cyan-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${
                        isPaidToDoctor ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      <FontAwesomeIcon icon={faUserDoctor} />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">Paid to Doctor</div>
                      <div className="text-xs text-slate-500">No Transmittal</div>
                      <div className="mt-1 text-xs leading-5 text-slate-500">
                        Always treated as doctor payout.
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <div
                className={`mt-5 rounded-xl border px-4 py-3 text-xs font-semibold ${
                  isPaidToDoctor
                    ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}
              >
                <div>Selected: {payoutSubLabel} - {payoutLabel}</div>
                <div className="mt-1 font-medium opacity-90">{payoutDescription}</div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsTransmittalConfirmOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirmTransmittalSelection}
                disabled={isProcessing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faClipboardCheck} />
                {isProcessing ? 'Loading...' : 'Continue to Process Summary'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isBatchRtpSaveConfirmOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Mark Claim as For RTP?</h3>
                <p className="text-sm text-slate-500">
                  Please confirm before saving this claim as Return To Provider.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchRtpSaveConfirmOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                Once saved, this claim status will become <strong>For RTP</strong>. It will
                remain visible inside Batch For Processing as an RTP item, but this batch cannot
                be submitted until an RTP Entry action has been created for this claim.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-slate-50 rounded-xl p-4">
                <div>
                  <span className="text-slate-500">TRN No</span>
                  <div className="font-semibold text-slate-700">{details?.trans_no || '-'}</div>
                </div>
                <div>
                  <span className="text-slate-500">LOA No</span>
                  <div className="font-semibold text-slate-700">{details?.loa_no || '-'}</div>
                </div>
                <div>
                  <span className="text-slate-500">Batch No</span>
                  <div className="font-semibold text-slate-700">{details?.batch_no || '-'}</div>
                </div>
                <div>
                  <span className="text-slate-500">RTP Reason</span>
                  <div className="font-semibold text-slate-700">{rtpReason || '-'}</div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsBatchRtpSaveConfirmOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirmBatchRtpSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faUndo} />
                Continue to Save Summary
              </button>
            </div>
          </div>
        </div>
      )}

      {isSaveSummaryOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Save Claim Summary</h3>
                <p className="text-sm text-slate-500">
                  Review the details below before confirming save.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsSaveSummaryOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-slate-50 rounded-xl p-4 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <span className="text-slate-500">Transaction No</span>
                    <div className="font-semibold text-slate-700">
                      {saveSummary?.trans_no || details?.trans_no || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">LOA No</span>
                    <div className="font-semibold text-slate-700">
                      {saveSummary?.loa_no || details?.loa_no || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Batch No</span>
                    <div className="font-semibold text-slate-700">
                      {saveSummary?.batch_no || details?.batch_no || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Claim Status</span>
                    <div className="font-semibold text-slate-700">
                      {saveSummary?.claim_status || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Claim Amount</span>
                    <div className="font-semibold text-slate-700">
                      {phpFormatter.format(parseNumber(saveSummary?.claim_amount))}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">RTP Tag</span>
                    <div className="font-semibold text-slate-700">
                      {saveSummary?.rtp_tag ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              </div>

              {renderClaimAmountConfirmation(saveSummary)}

              <div className="bg-white rounded-xl border border-slate-100 p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Steps</h4>
                <ul className="space-y-2 text-sm">
                  {saveSteps.length === 0 ? (
                    <li className="text-slate-500">No steps available.</li>
                  ) : (
                    saveSteps.map((step, index) => (
                      <li
                        key={`${step.name}-${index}`}
                        className="flex items-start justify-between gap-3 border border-slate-100 rounded-lg p-3"
                      >
                        <div>
                          <div className="font-semibold text-slate-700">{step.name}</div>
                          {step.details && (
                            <div className="text-xs text-slate-500 mt-1">
                              {JSON.stringify(step.details)}
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            step.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {step.success ? 'Ready' : 'Error'}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsSaveSummaryOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirmSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faSave} />
                {isSaving ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isProcessSummaryOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Process Claim Summary</h3>
                <p className="text-sm text-slate-500">
                  Review the details below before confirming process.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsProcessSummaryOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-slate-50 rounded-xl p-4 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <span className="text-slate-500">Transaction No</span>
                    <div className="font-semibold text-slate-700">
                      {processSummary?.trans_no || details?.trans_no || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">LOA No</span>
                    <div className="font-semibold text-slate-700">
                      {processSummary?.loa_no || details?.loa_no || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Batch No</span>
                    <div className="font-semibold text-slate-700">
                      {processSummary?.batch_no || details?.batch_no || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Claim Status</span>
                    <div className="font-semibold text-slate-700">
                      {processSummary?.claim_status || '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Claim Amount</span>
                    <div className="font-semibold text-slate-700">
                      {phpFormatter.format(parseNumber(processSummary?.claim_amount))}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">RTP Tag</span>
                    <div className="font-semibold text-slate-700">
                      {processSummary?.rtp_tag ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Cancelled Override</span>
                    <div className="font-semibold text-slate-700">
                      {shouldOverrideCancelled ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              </div>

              {renderClaimAmountConfirmation(processSummary)}

              <div className="bg-white rounded-xl border border-slate-100 p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Steps</h4>
                <ul className="space-y-2 text-sm">
                  {processSteps.length === 0 ? (
                    <li className="text-slate-500">No steps available.</li>
                  ) : (
                    processSteps.map((step, index) => (
                      <li
                        key={`${step.name}-${index}`}
                        className="flex items-start justify-between gap-3 border border-slate-100 rounded-lg p-3"
                      >
                        <div>
                          <div className="font-semibold text-slate-700">{step.name}</div>
                          {step.details && (
                            <div className="text-xs text-slate-500 mt-1">
                              {JSON.stringify(step.details)}
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            step.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {step.success ? 'Ready' : 'Error'}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsProcessSummaryOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirmProcess}
                disabled={isProcessing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faClipboardCheck} />
                {isProcessing ? 'Processing...' : 'Confirm & Process'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isProviderPaymentSetupModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Provider Payment Set-up
                </h3>
                <p className="text-sm text-slate-500">
                  {(providerPaymentSetupProvider?.provider_name || details?.provider_name || '-')}
                  {' '}
                  ({providerPaymentSetupProvider?.provider_code || details?.provider_code || claim?.provider_code || '-'})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsProviderPaymentSetupModalOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {isLoadingProviderPaymentSetup ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  Loading provider payment set-up...
                </div>
              ) : providerPaymentSetupRows.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  No provider payment setup found for this provider.
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700">Provider Set-up</h4>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {providerPaymentSetupDisplayRows.map((row) => (
                        <div
                          key={row.key}
                          className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 px-4 py-3 text-sm"
                        >
                          <div className="font-semibold text-slate-600">{row.label}:</div>
                          <div className="text-slate-800">{row.value || '-'}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700">Details</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-white text-xs uppercase text-slate-500">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold">Coverage Setup</th>
                            <th className="px-4 py-3 text-left font-semibold">Whole</th>
                            <th className="px-4 py-3 text-left font-semibold">Split HB</th>
                            <th className="px-4 py-3 text-left font-semibold">Split PF</th>
                            <th className="px-4 py-3 text-left font-semibold">Updated By</th>
                            <th className="px-4 py-3 text-left font-semibold">Last Update</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {providerPaymentSetupRows.map((row) => (
                            <tr key={row.tbl_id || row.provider_coverage_setup}>
                              <td className="px-4 py-3 font-semibold text-slate-700">
                                {row.provider_coverage_setup || '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {row.whole_tag_label || '0'}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {row.split_hb_tag_label || '0'}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {row.split_pf_tag_label || '0'}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {row.update_by || '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {formatTrackerDateTime(row.lastupdate_date)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => setIsProviderPaymentSetupModalOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isTrackerRemarksModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Tracker Remarks</h3>
                <p className="text-sm text-slate-500">
                  Showing tracker remarks and disapproved remarks matched by Transaction No or LOA No.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsTrackerRemarksModalOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {isLoadingTrackerRemarks ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  Loading tracker remarks...
                </div>
              ) : trackerRemarksRows.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  No tracker remarks found for this claim.
                </div>
              ) : (
                trackerRemarksRows.map((row, index) => (
                  <div
                    key={`${row.tbl_id || 'tracker'}-${index}`}
                    className="rounded-xl border border-slate-200 p-4 space-y-3"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-slate-500">Tracker ID</span>
                        <div className="font-semibold text-slate-700">{row.tbl_id || '-'}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Last Update</span>
                        <div className="font-semibold text-slate-700">
                          {formatTrackerDateTime(row.datetime_last_update || row.datetime_created)}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-500">TRN No</span>
                        <div className="font-semibold text-slate-700">{row.trans_no || '-'}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">LOA No</span>
                        <div className="font-semibold text-slate-700">{row.loa_no || '-'}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="text-xs font-semibold text-slate-600 mb-1">Remarks</div>
                        <textarea
                          rows={5}
                          readOnly
                          value={row.remarks || ''}
                          placeholder="-"
                          className="w-full px-3 py-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg resize-y"
                        />
                        <button
                          type="button"
                          onClick={() => handleCopyTrackerField(row.remarks, 'Tracker remarks')}
                          className="mt-2 inline-flex items-center px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100"
                        >
                          Copy Remarks
                        </button>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="text-xs font-semibold text-slate-600 mb-1">
                          Disapproved Remarks
                        </div>
                        <textarea
                          rows={5}
                          readOnly
                          value={row.disapproved_remarks || ''}
                          placeholder="-"
                          className="w-full px-3 py-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg resize-y"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleCopyTrackerField(
                              row.disapproved_remarks,
                              'Disapproved remarks'
                            )
                          }
                          className="mt-2 inline-flex items-center px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100"
                        >
                          Copy Disapproved Remarks
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => setIsTrackerRemarksModalOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isOverrideModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Override Cancelled Claim</h3>
                <p className="text-sm text-slate-500">
                  Do you want to override this Cancelled Claim TRN/LOA? If you override, the
                  transaction flag status will be updated to '02' (Approved TRN with LOA) and this
                  claim can proceed for processing.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOverrideModalOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                Remarks are required before you can override this claim.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-slate-50 rounded-xl p-4">
                <div>
                  <span className="text-slate-500">TRN No</span>
                  <div className="font-semibold text-slate-700">{details?.trans_no || '-'}</div>
                </div>
                <div>
                  <span className="text-slate-500">LOA No</span>
                  <div className="font-semibold text-slate-700">{details?.loa_no || '-'}</div>
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-500">
                  Override Remarks <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={overrideRemarks}
                  onChange={(e) => setOverrideRemarks(e.target.value)}
                  rows={4}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Enter reason for override..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsOverrideModalOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCancelledOverride}
                disabled={isProcessing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faClipboardCheck} />
                Confirm Override
              </button>
            </div>
          </div>
        </div>
      )}

      {isRtpModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Return To Provider</h3>
                <p className="text-sm text-slate-500">Review details and provide remarks.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsRtpModalOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">LOA No</span>
                  <span className="font-semibold text-slate-700">{details?.loa_no || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Transaction No</span>
                  <span className="font-semibold text-slate-700">{details?.trans_no || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Batch No</span>
                  <span className="font-semibold text-slate-700">{details?.batch_no || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Member</span>
                  <span className="font-semibold text-slate-700">
                    {details?.patient_name || details?.member_name || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Provider</span>
                  <span className="font-semibold text-slate-700">{details?.provider_name || '-'}</span>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-500">Remarks / Reason</label>
                  <textarea
                    value={rtpModalRemarks}
                    onChange={(e) => setRtpModalRemarks(e.target.value)}
                    rows={4}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-500">Upload Attachments (optional, max 2)</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleRtpFileChange(e.target.files)}
                    className="mt-2"
                  />
                  {rtpUploadQueue.length > 0 && (
                    <div className="mt-3 space-y-2 text-xs">
                      {rtpUploadQueue.map((item) => (
                        <div key={item.file.name} className="bg-slate-50 rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="truncate">{item.file.name}</span>
                            {item.isUploading && (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            )}
                          </div>
                          <div className="mt-2 h-2 bg-slate-200 rounded-full">
                            <div
                              className="h-2 bg-emerald-500 rounded-full"
                              style={{ width: `${item.progress}%` }}
                            />
                          </div>
                          {item.error && (
                            <p className="text-red-500 mt-1">Error: {item.error}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {rtpUploadFiles.length > 0 && (
                    <ul className="mt-3 space-y-2 text-xs">
                      {rtpUploadFiles.map((file) => (
                        <li
                          key={file.upload_id}
                          className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg"
                        >
                          <span className="truncate">{file.original_file_name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveRtpAttachment(file)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsRtpModalOpen(false)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitReturnToProvider}
                disabled={isSubmittingRtp}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faUndo} />
                {isSubmittingRtp ? 'Submitting...' : 'Confirm Return'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessClaimModal;
